import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../AuthContext';
import OfficeBookingDrawer from '../components/OfficeBookingDrawer';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { compareUnitNumbers } from '../utils/unitSort';
import { computeGstAmountForUnit } from '../office/gst';
import {
  type BankTransactionRow,
  getBookingPendingAmount,
  getFinancialYearKey,
  isCustomerReceiptTxn,
  isCustomerRefundTxn,
  isExcludedFromBookingReport,
  parseDateLike,
  resolveTxnDateLike,
  sumActiveBookingReceiptsByUnit,
  toMs,
} from '../office/today/collections';

type UnitRow = {
  id: string;
  unitNumber?: string | null;
  unitNumberLower?: string | null;
  blockType?: string | null;
  usage?: string | null;
  carpetArea?: number | null;
  exclusiveBalconyArea?: number | null;
  exclusiveWashArea?: number | null;
  area_of_exclusive_wash?: number | null;
  area_of_exclusive_open_terrace?: number | null;
  openTerraceArea?: number | null;
  customFields?: Record<string, any> | null;
  unitAmount?: number | null;
  bookingStatus?: string | null;
  activeBookingId?: string | null;
  saleAgreementDate?: string | null;
  encumbranceStatus?: string | null;
  redevelopment?: string | null;
  latestBookingFormDate?: any;
  bookingFormDate?: any;
  primaryCustomerName?: string | null;
  customerName?: string | null;
  allotteeName?: string | null;
  primaryMobile?: string | null;
  primaryCustomerMobile?: string | null;
  mobileNumber?: string | null;
  customerMobile?: string | null;
  loanToDeveloperAmount?: number | null;
  isActive?: boolean | null;
};

type BookingCustomerRow = {
  id: string;
  displayName?: string | null;
  fullName?: string | null;
  customerName?: string | null;
  allotteeName?: string | null;
  name?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  surname?: string | null;
  lastName?: string | null;
  role?: string | null;
  customerType?: string | null;
  isPrimary?: boolean | null;
  mobile?: string | null;
  mobileNo?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  contactNo?: string | null;
  whatsapp?: string | null;
  pan?: string | null;
  aadhaar?: string | null;
  aadhar?: string | null;
  aadhaarNo?: string | null;
  aadharNo?: string | null;
  createdAt?: any;
};

type KycDocRow = {
  id: string;
  type?: string | null;
  docNumber?: string | null;
};

type KycInfo = {
  type: string;
  no: string;
};

type BookingInfo = {
  names: string[];
  primaryName: string;
  primaryMobile: string;
  kyc: KycInfo;
  bookingId?: string;
  bookingCreatedAtMs?: number;
  bookingHistoryCount?: number;
  agreementDateRaw?: string;
  saleDeedNumber?: string;
  saleDeedDateRaw?: string;
  saleDeedDocumentId?: string;
};



const pad2 = (value: number) => (value < 10 ? `0${value}` : String(value));

const formatDDMMYYYY = (value: any): string => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '—';
  return `${pad2(parsed.getDate())}-${pad2(parsed.getMonth() + 1)}-${parsed.getFullYear()}`;
};

const formatAmount2 = (value: number): string =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


const toYMD = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const toYM = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const formatAxisLabel = (date: Date, mode: 'day' | 'month'): string =>
  mode === 'day'
    ? `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`
    : `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;

const formatAxisFullLabel = (date: Date, mode: 'day' | 'month'): string =>
  mode === 'day'
    ? `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`
    : `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;

const getTodayYMD = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};


const getCurrentFinancialYearKey = (): string => getFinancialYearKey(new Date());


const xmlEscape = (value: any): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const xlsxColumnName = (index: number): string => {
  let n = index + 1;
  let name = '';
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
};

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (view: DataView, offset: number, value: number) => view.setUint16(offset, value, true);
const writeUint32 = (view: DataView, offset: number, value: number) => view.setUint32(offset, value, true);

const concatUint8Arrays = (parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

const createStoredZipBlob = (files: { name: string; content: string }[], mimeType: string): Blob => {
  const encoder = new TextEncoder();
  const fileParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content);
    const crc = crc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, 0);
    writeUint16(localView, 12, 0);
    writeUint32(localView, 14, crc);
    writeUint32(localView, 18, dataBytes.length);
    writeUint32(localView, 22, dataBytes.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    localHeader.set(nameBytes, 30);
    fileParts.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, 0);
    writeUint16(centralView, 14, 0);
    writeUint32(centralView, 16, crc);
    writeUint32(centralView, 20, dataBytes.length);
    writeUint32(centralView, 24, dataBytes.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralParts.push(centralHeader);

    offset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatUint8Arrays(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, files.length);
  writeUint16(endView, 10, files.length);
  writeUint32(endView, 12, centralDirectory.length);
  writeUint32(endView, 16, offset);
  writeUint16(endView, 20, 0);

  return new Blob([concatUint8Arrays([...fileParts, centralDirectory, endRecord])], { type: mimeType });
};

const createXlsxBlob = (sheetName: string, headers: string[], rows: (string | number)[][]): Blob => {
  const cellXml = (value: string | number, rowIndex: number, colIndex: number): string => {
    const ref = `${xlsxColumnName(colIndex)}${rowIndex}`;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `<c r="${ref}"><v>${value}</v></c>`;
    }
    return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;
  };
  const rowXml = [headers, ...rows]
    .map((row, rowIndex) => {
      const excelRow = rowIndex + 1;
      return `<row r="${excelRow}">${row.map((value, colIndex) => cellXml(value, excelRow, colIndex)).join('')}</row>`;
    })
    .join('');
  const colsXml = headers
    .map((header, index) => {
      const width = Math.max(12, Math.min(36, String(header || '').length + 4));
      return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
    })
    .join('');
  const worksheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><cols>${colsXml}</cols><sheetData>${rowXml}</sheetData></worksheet>`;
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xmlEscape(sheetName).slice(0, 31)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`;
  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs></styleSheet>`;

  return createStoredZipBlob(
    [
      { name: '[Content_Types].xml', content: contentTypes },
      { name: '_rels/.rels', content: rootRels },
      { name: 'xl/workbook.xml', content: workbook },
      { name: 'xl/_rels/workbook.xml.rels', content: workbookRels },
      { name: 'xl/styles.xml', content: styles },
      { name: 'xl/worksheets/sheet1.xml', content: worksheet },
    ],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


const resolveCustomerName = (row: BookingCustomerRow): string => {
  return String(
    row.displayName ||
      row.fullName ||
      row.customerName ||
      row.allotteeName ||
      row.name ||
      [row.firstName, row.middleName, row.surname || row.lastName].filter(Boolean).join(' ')
  ).trim();
};

const resolveCustomerMobile = (row?: BookingCustomerRow | null): string =>
  String(row?.mobile || row?.mobileNo || row?.phone || row?.phoneNumber || row?.contactNo || row?.whatsapp || '').trim();

const joinNamesWithAnd = (names: string[]): string => {
  const clean = (names || []).map((name) => String(name || '').trim()).filter(Boolean);
  if (!clean.length) return '—';
  if (clean.length === 1) return clean[0];
  return clean.join(' and ');
};

const resolveKycFromDocs = (
  docs: KycDocRow[],
  fallback?: { pan?: string | null; aadhaar?: string | null }
): KycInfo => {
  let pan = '';
  let aadhaar = '';
  docs.forEach((d) => {
    const type = String(d.type || d.id || '').trim().toUpperCase();
    const no = String(d.docNumber || '').trim();
    if (!no) return;
    if (type === 'PAN' && !pan) pan = no;
    if (type === 'AADHAAR' && !aadhaar) aadhaar = no;
  });
  if (!pan) pan = String(fallback?.pan || '').trim();
  if (!aadhaar) aadhaar = String(fallback?.aadhaar || '').trim();
  if (pan) return { type: 'PAN', no: pan };
  if (aadhaar) return { type: 'AADHAAR', no: aadhaar };
  return { type: '—', no: '—' };
};

const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const coerceDateTokenValue = (value: any): string => {
  if (!value && value !== 0) return '';
  const parsed = parseDateLike(value);
  if (parsed) return toYmd(parsed);
  const text = String(value || '').trim();
  return text;
};

const resolveTextTokenFromBooking = (booking: any, keys: string[]): string => {
  if (!booking || typeof booking !== 'object') return '';
  for (const key of keys) {
    const value = String((booking as any)?.[key] || '').trim();
    if (value) return value;
  }
  const stepData = (booking as any)?.stepData;
  if (stepData && typeof stepData === 'object') {
    const entries = Object.values(stepData as Record<string, any>);
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      for (const key of keys) {
        const value = String((entry as any)?.[key] || '').trim();
        if (value) return value;
      }
    }
  }
  return '';
};

const resolveDateTokenFromBooking = (booking: any, keys: string[]): string => {
  if (!booking || typeof booking !== 'object') return '';
  for (const key of keys) {
    const value = coerceDateTokenValue((booking as any)?.[key]);
    if (value) return value;
  }
  const stepData = (booking as any)?.stepData;
  if (stepData && typeof stepData === 'object') {
    const entries = Object.values(stepData as Record<string, any>);
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      for (const key of keys) {
        const value = coerceDateTokenValue((entry as any)?.[key]);
        if (value) return value;
      }
    }
  }
  return '';
};

const resolveAgreementDateFromBooking = (booking: any): string =>
  resolveDateTokenFromBooking(booking, ['sale_agreement_date', 'saleAgreementDate', 'agreement_date', 'agreementDate']);

const resolveSaleDeedNumberFromBooking = (booking: any): string =>
  resolveTextTokenFromBooking(booking, ['sale_deed_number', 'saleDeedNumber']);

const resolveSaleDeedDateFromBooking = (booking: any): string =>
  resolveDateTokenFromBooking(booking, ['sale_deed_date', 'saleDeedDate']);

const resolveUnitBuilding = (unitNo: string): string => {
  const text = String(unitNo || '').trim();
  const match = text.match(/^([A-Za-z]+)-/);
  return match ? match[1].toUpperCase() : 'Other';
};

export default function OfficeReportsPage() {
  const { currentProjectId } = useAuth();
  const db = useMemo(() => getFirestore(), []);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactLayout = useMediaQuery(theme.breakpoints.down('lg'));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [bookingInfoByUnitId, setBookingInfoByUnitId] = useState<Map<string, BookingInfo>>(new Map());
  const [bankTxns, setBankTxns] = useState<BankTransactionRow[]>([]);
  const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);
  const [bookingUnit, setBookingUnit] = useState<UnitRow | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportTillDateYMD, setExportTillDateYMD] = useState(() => getTodayYMD());
  const [projectMeta, setProjectMeta] = useState<any | null>(null);
  const [companyMeta, setCompanyMeta] = useState<any | null>(null);
  const [exportPdfBusy, setExportPdfBusy] = useState(false);
  const [exportPdfError, setExportPdfError] = useState<string | null>(null);
  const [reportType, setReportType] = useState<'booking' | 'rera' | 'gst' | 'contacts'>('booking');
  const [contactBuildingFilter, setContactBuildingFilter] = useState('all');
  const [contactUsageFilter, setContactUsageFilter] = useState('all');
  const [bookingTrendHoverIndex, setBookingTrendHoverIndex] = useState<number | null>(null);
  const [bookingTrendSelectedIndex, setBookingTrendSelectedIndex] = useState<number | null>(null);
  const [selectedBookingTrendCutoffMs, setSelectedBookingTrendCutoffMs] = useState<number | null>(null);
  const [bookingPeriodMode, setBookingPeriodMode] = useState<'till_date' | 'financial_year'>('till_date');
  const [bookingFinancialYear, setBookingFinancialYear] = useState(() => getCurrentFinancialYearKey());
  const [bookingColumnAnchor, setBookingColumnAnchor] = useState<HTMLElement | null>(null);
  const [bookingColumns, setBookingColumns] = useState({ totalAmount: false, pendingAmount: false, gstStatus: false });
  const [bookingLoanOnly, setBookingLoanOnly] = useState(false);
  const [bookingExportColumns, setBookingExportColumns] = useState({ kycType: true, kycNo: true });
  const [gstMonthMode, setGstMonthMode] = useState<'all' | 'month' | 'financial_year'>('all');
  const [gstTopFilter, setGstTopFilter] = useState<'all' | 'amount' | 'paid' | 'balance'>('all');
  const [gstMonthYMD, setGstMonthYMD] = useState(() => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
  });
  const [gstFinancialYear, setGstFinancialYear] = useState(() => getCurrentFinancialYearKey());

  useEffect(() => {
    setExportTillDateYMD(getTodayYMD());
  }, []);

  useEffect(() => {
    if (!currentProjectId) {
      setUnits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    const q = query(
      collection(db, 'projects', currentProjectId, 'units'),
      orderBy('unitNumberLower', 'asc'),
      limit(5000)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => row?.isActive !== false)
          .sort((a: any, b: any) => compareUnitNumbers(a?.unitNumber || a?.id, b?.unitNumber || b?.id)) as UnitRow[];
        setUnits(next);
        setLoading(false);
      },
      (err) => {
        console.error('Load units for reports failed', err);
        setLoadError(err?.message || 'Failed to load units.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentProjectId, db]);

  useEffect(() => {
    let cancelled = false;
    const loadProjectAndCompany = async () => {
      if (!currentProjectId) {
        setProjectMeta(null);
        setCompanyMeta(null);
        return;
      }
      try {
        const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
        const projectData = projectSnap.exists() ? projectSnap.data() : null;
        if (cancelled) return;
        setProjectMeta(projectData);
        const companyProfileId = String(projectData?.companyProfileId || '').trim();
        if (companyProfileId) {
          const companySnap = await getDoc(doc(db, 'companyProfiles', companyProfileId));
          if (cancelled) return;
          setCompanyMeta(companySnap.exists() ? companySnap.data() : null);
          return;
        }
        const fallbackCompanySnap = await getDocs(query(collection(db, 'companyProfiles'), limit(1)));
        if (cancelled) return;
        setCompanyMeta(fallbackCompanySnap.docs[0]?.data() || null);
      } catch (err) {
        if (cancelled) return;
        console.error('Load project/company metadata failed', err);
      }
    };
    loadProjectAndCompany();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db]);

  useEffect(() => {
    if (!currentProjectId) {
      setBankTxns([]);
      return;
    }
    const q = query(collection(db, 'projects', currentProjectId, 'bankTransactions'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const txns = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => row?.isActive !== false) as BankTransactionRow[];
        setBankTxns(txns);
      },
      (err) => {
        console.error('Load customer receipts for reports failed', err);
        setLoadError((prev) => prev || err?.message || 'Failed to load customer receipt totals.');
      }
    );
    return () => unsub();
  }, [currentProjectId, db]);

  const baseBookingTillMs = useMemo(() => {
    const tillDate = parseDateLike(exportTillDateYMD);
    if (!tillDate || Number.isNaN(tillDate.getTime())) return Number.POSITIVE_INFINITY;
    return new Date(tillDate.getFullYear(), tillDate.getMonth(), tillDate.getDate(), 23, 59, 59, 999).getTime();
  }, [exportTillDateYMD]);

  const financialYearOptions = useMemo(() => {
    const keys = new Set<string>();
    const currentFy = getCurrentFinancialYearKey();
    keys.add(currentFy);
    const currentStartYear = Number(currentFy.slice(0, 4));
    if (Number.isFinite(currentStartYear)) {
      for (let offset = -4; offset <= 2; offset += 1) {
        const startYear = currentStartYear + offset;
        keys.add(`${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`);
      }
    }
    bankTxns.forEach((txn) => {
      const txnDate = resolveTxnDateLike(txn);
      if (txnDate && (isCustomerReceiptTxn(txn) || isCustomerRefundTxn(txn) || String(txn.receiptCategory || '').toLowerCase().trim() === 'gst')) {
        keys.add(getFinancialYearKey(txnDate));
      }
    });
    return Array.from(keys).sort((a, b) => b.localeCompare(a));
  }, [bankTxns]);

  const getFinancialYearRange = useCallback((yearKey: string): { startMs: number; endMs: number } | null => {
    const startYear = Number(String(yearKey || '').slice(0, 4));
    if (!Number.isFinite(startYear) || startYear <= 0) return null;
    return {
      startMs: new Date(startYear, 3, 1, 0, 0, 0, 0).getTime(),
      endMs: new Date(startYear + 1, 2, 31, 23, 59, 59, 999).getTime(),
    };
  }, []);

  const bookingReceiptAmountByUnitId = useMemo(
    () =>
      sumActiveBookingReceiptsByUnit(
        bankTxns,
        units,
        bookingInfoByUnitId,
        Number.NEGATIVE_INFINITY,
        Number(baseBookingTillMs || Number.POSITIVE_INFINITY)
      ),
    [bankTxns, baseBookingTillMs, bookingInfoByUnitId, units]
  );

  const bookingReceiptAmountByUnitIdForTable = useMemo(
    () =>
      sumActiveBookingReceiptsByUnit(
        bankTxns,
        units,
        bookingInfoByUnitId,
        Number.NEGATIVE_INFINITY,
        Number(selectedBookingTrendCutoffMs || baseBookingTillMs || Number.POSITIVE_INFINITY)
      ),
    [bankTxns, baseBookingTillMs, bookingInfoByUnitId, selectedBookingTrendCutoffMs, units]
  );

  const calculateActiveBookingCustomerReceiptAmounts = useCallback(
    (startMs: number, endMs: number) => sumActiveBookingReceiptsByUnit(bankTxns, units, bookingInfoByUnitId, startMs, endMs),
    [bankTxns, bookingInfoByUnitId, units]
  );

  const bookingMisReceiptAmountByUnitId = useMemo(() => {
    if (bookingPeriodMode !== 'financial_year') return bookingReceiptAmountByUnitId;
    const range = getFinancialYearRange(bookingFinancialYear);
    if (!range) return new Map<string, number>();
    return calculateActiveBookingCustomerReceiptAmounts(range.startMs, range.endMs);
  }, [
    bookingFinancialYear,
    bookingPeriodMode,
    bookingReceiptAmountByUnitId,
    calculateActiveBookingCustomerReceiptAmounts,
    getFinancialYearRange,
  ]);

  const bookingMisReceiptAmountByUnitIdForTable = useMemo(() => {
    if (bookingPeriodMode !== 'financial_year') return bookingReceiptAmountByUnitIdForTable;
    const range = getFinancialYearRange(bookingFinancialYear);
    if (!range) return new Map<string, number>();
    return calculateActiveBookingCustomerReceiptAmounts(range.startMs, range.endMs);
  }, [
    bookingFinancialYear,
    bookingPeriodMode,
    bookingReceiptAmountByUnitIdForTable,
    calculateActiveBookingCustomerReceiptAmounts,
    getFinancialYearRange,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadBookingDetails = async () => {
      if (!currentProjectId) {
        setBookingInfoByUnitId(new Map());
        return;
      }
      const unitsWithActiveBooking = units.filter((row) => String(row.activeBookingId || '').trim());
      if (!unitsWithActiveBooking.length) {
        setBookingInfoByUnitId(new Map());
        return;
      }
      try {
        const next = new Map<string, BookingInfo>();
        await Promise.all(
          unitsWithActiveBooking.map(async (unit) => {
            const unitId = String(unit.id || '').trim();
            const bookingId = String(unit.activeBookingId || '').trim();
            if (!unitId || !bookingId) return;
            const bookingsSnap = await getDocs(
              query(collection(db, 'projects', currentProjectId, 'units', unitId, 'bookings'), limit(50))
            );
            const bookingSnap = await getDoc(
              doc(db, 'projects', currentProjectId, 'units', unitId, 'bookings', bookingId)
            );
            const bookingData = bookingSnap.exists() ? bookingSnap.data() : null;
            const agreementDateRaw = resolveAgreementDateFromBooking(bookingData);
            const saleDeedNumber = resolveSaleDeedNumberFromBooking(bookingData);
            const saleDeedDateRawFromBooking = resolveSaleDeedDateFromBooking(bookingData);
            const saleDeedDocSnaps = await Promise.all(
              ['sale_deed', 'sale_deed_upload', 'sale_deed_signed'].map((docId) =>
                getDoc(
                  doc(
                    db,
                    'projects',
                    currentProjectId,
                    'units',
                    unitId,
                    'bookings',
                    bookingId,
                    'uploaded',
                    docId
                  )
                )
              )
            );
            const saleDeedDocSnap =
              saleDeedDocSnaps.find((snap) => {
                if (!snap.exists()) return false;
                const files = (snap.data() as any)?.files;
                return !Array.isArray(files) || files.length > 0;
              }) || null;
            const saleDeedDoc = saleDeedDocSnap?.exists() ? (saleDeedDocSnap.data() as any) : null;
            const saleDeedUploadedAtMs =
              Array.isArray(saleDeedDoc?.files) && saleDeedDoc.files[0]?.uploadedAtMs
                ? saleDeedDoc.files[0].uploadedAtMs
                : '';
            const saleDeedDateRaw = saleDeedDateRawFromBooking || saleDeedUploadedAtMs;

            const customersSnap = await getDocs(
              collection(db, 'projects', currentProjectId, 'units', unitId, 'bookings', bookingId, 'customers')
            );
            const customers = customersSnap.docs
              .map((d) => ({ id: d.id, ...(d.data() as any) })) as BookingCustomerRow[];
            customers.sort((a, b) => {
              const aPrimary =
                String(a.role || a.customerType || '').toLowerCase() === 'primary' || a.isPrimary === true ? 0 : 1;
              const bPrimary =
                String(b.role || b.customerType || '').toLowerCase() === 'primary' || b.isPrimary === true ? 0 : 1;
              if (aPrimary !== bPrimary) return aPrimary - bPrimary;
              return toMs(a.createdAt) - toMs(b.createdAt);
            });

            const names = customers.map(resolveCustomerName).filter(Boolean);
            const primaryCustomer =
              customers.find((row) => String(row.role || row.customerType || '').toLowerCase() === 'primary') ||
              customers.find((row) => row.isPrimary === true) ||
              customers[0] ||
              null;
            const primaryName = primaryCustomer ? resolveCustomerName(primaryCustomer) : '';
            const primaryMobile = resolveCustomerMobile(primaryCustomer);
            const customerKyc = await Promise.all(
              customers.map(async (customer) => {
                const kycSnap = await getDocs(
                  collection(
                    db,
                    'projects',
                    currentProjectId,
                    'units',
                    unitId,
                    'bookings',
                    bookingId,
                    'customers',
                    customer.id,
                    'kycDocs'
                  )
                );
                const docs = kycSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as KycDocRow[];
                const fallbackPan = String(customer.pan || '').trim();
                const fallbackAadhaar = String(
                  customer.aadhaar || customer.aadhar || customer.aadhaarNo || customer.aadharNo || ''
                ).trim();
                return {
                  customerId: customer.id,
                  role: String(customer.role || '').toLowerCase(),
                  kyc: resolveKycFromDocs(docs, {
                    pan: fallbackPan,
                    aadhaar: fallbackAadhaar,
                  }),
                };
              })
            );

            const preferred =
              customerKyc.find((row) => row.role === 'primary' && row.kyc.type !== '—') ||
              customerKyc.find((row) => row.kyc.type !== '—');

            next.set(unitId, {
              names,
              primaryName,
              primaryMobile,
              kyc: preferred?.kyc || { type: '—', no: '—' },
              bookingId,
              bookingCreatedAtMs: toMs(bookingData?.createdAt),
              bookingHistoryCount: bookingsSnap.size,
              agreementDateRaw,
              saleDeedNumber,
              saleDeedDateRaw,
              saleDeedDocumentId: saleDeedDocSnap?.exists() ? saleDeedDocSnap.id : '',
            });
          })
        );
        if (cancelled) return;
        setBookingInfoByUnitId(next);
      } catch (err) {
        if (cancelled) return;
        console.error('Load booking details for reports failed', err);
        setLoadError((prev) => prev || 'Failed to load booking/customer details for report.');
      }
    };
    loadBookingDetails();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db, units]);

  const bookingGstStatusByUnitId = useMemo(() => {
    const activeBookingByUnitId = new Map<string, string>();
    const activeBookingStartByUnitId = new Map<string, number>();
    const gstPaidByUnitId = new Map<string, number>();
    units.forEach((unit) => {
      const unitId = String(unit.id || '').trim();
      const bookingId = String(unit.activeBookingId || '').trim();
      if (unitId && bookingId) activeBookingByUnitId.set(unitId, bookingId);
      const bookingCreatedAtMs = Number(bookingInfoByUnitId.get(unitId)?.bookingCreatedAtMs || 0);
      if (unitId && bookingCreatedAtMs > 0) activeBookingStartByUnitId.set(unitId, bookingCreatedAtMs);
    });
    bankTxns.forEach((txn) => {
      if (isExcludedFromBookingReport(txn)) return;
      if (String(txn.txnType || '').toLowerCase() !== 'receipt') return;
      if (String(txn.receiptCategory || txn.receiptFor || '').toLowerCase().trim() !== 'gst') return;
      const unitId = String(txn.unitId || txn.unitID || '').trim();
      const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
      if (!unitId || !activeBookingId) return;
      const txnBookingId = String(txn.bookingId || '').trim();
      if (txnBookingId && txnBookingId !== activeBookingId) return;
      const txnDate = resolveTxnDateLike(txn);
      if (!txnDate) return;
      if (!txnBookingId) {
        const bookingHistoryCount = Number(bookingInfoByUnitId.get(unitId)?.bookingHistoryCount || 0);
        const activeBookingStartMs = Number(activeBookingStartByUnitId.get(unitId) || 0);
        if (bookingHistoryCount !== 1 && activeBookingStartMs > 0 && txnDate.getTime() < activeBookingStartMs) return;
      }
      const amount = Number(txn.amount || 0);
      if (Number.isFinite(amount) && amount > 0) {
        gstPaidByUnitId.set(unitId, Number(gstPaidByUnitId.get(unitId) || 0) + amount);
      }
    });
    const statusByUnitId = new Map<string, 'paid' | 'pending'>();
    units.forEach((unit) => {
      const unitId = String(unit.id || '').trim();
      if (!unitId || !activeBookingByUnitId.has(unitId)) return;
      const explicitGst = Number((unit as any)?.gst ?? (unit as any)?.gstAmount ?? (unit as any)?.gst_amount ?? NaN);
      const computedGst = computeGstAmountForUnit(Number(unit.unitAmount || 0), unit as any, (projectMeta as any) || null, 1);
      const gstAmount = Number.isFinite(explicitGst) && explicitGst > 0 ? explicitGst : computedGst;
      statusByUnitId.set(unitId, gstAmount > 0 && Number(gstPaidByUnitId.get(unitId) || 0) >= gstAmount ? 'paid' : 'pending');
    });
    return statusByUnitId;
  }, [bankTxns, bookingInfoByUnitId, projectMeta, units]);

  const baseRows = useMemo(() => {
    return units
      .map((unit) => {
        const unitId = String(unit.id || '').trim();
        const info = bookingInfoByUnitId.get(unitId);
        const receivedAmount = Number(bookingMisReceiptAmountByUnitId.get(unitId) || 0);
        const unitAmount = Number(unit.unitAmount || 0);
        return {
          unitId,
          unitNo: String(unit.unitNumber || '—').trim() || '—',
          names: info?.names?.length ? info.names.join(', ') : '—',
          agreementDateRaw: String(info?.agreementDateRaw || unit.saleAgreementDate || '').trim(),
          agreementDate: formatDDMMYYYY(info?.agreementDateRaw || unit.saleAgreementDate),
          kycType: info?.kyc?.type || '—',
          kycNo: info?.kyc?.no || '—',
          unitAmount,
          loanToDeveloperAmount: Math.max(0, Number(unit.loanToDeveloperAmount || 0) || 0),
          receivedAmount,
          pendingAmount: getBookingPendingAmount(unitAmount, receivedAmount),
          gstStatus: bookingGstStatusByUnitId.get(unitId) || 'pending',
          saleDeedNumber: String(info?.saleDeedNumber || '').trim(),
          saleDeedDateRaw: String(info?.saleDeedDateRaw || '').trim(),
          saleDeedDate: formatDDMMYYYY(info?.saleDeedDateRaw || ''),
          saleDeedDocumentId: String(info?.saleDeedDocumentId || '').trim(),
        };
      })
      .filter((row) => row.receivedAmount > 0);
  }, [bookingGstStatusByUnitId, bookingInfoByUnitId, bookingMisReceiptAmountByUnitId, units]);

  const filteredBaseRows = useMemo(() => {
    const loanFilteredRows = bookingLoanOnly
      ? baseRows.filter((row) => Number(row.loanToDeveloperAmount || 0) > 0)
      : baseRows;
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return loanFilteredRows;
    return loanFilteredRows.filter((row) =>
      [row.unitNo, row.names, row.agreementDate, row.kycType, row.kycNo].some((value) =>
        String(value || '').toLowerCase().includes(needle)
      )
    );
  }, [baseRows, bookingLoanOnly, search]);

  const rows = useMemo(() => {
    return units
      .map((unit) => {
        const unitId = String(unit.id || '').trim();
        const info = bookingInfoByUnitId.get(unitId);
        const receivedAmount = Number(bookingMisReceiptAmountByUnitIdForTable.get(unitId) || 0);
        const unitAmount = Number(unit.unitAmount || 0);
        return {
          unitId,
          unitNo: String(unit.unitNumber || '—').trim() || '—',
          names: info?.names?.length ? info.names.join(', ') : '—',
          agreementDateRaw: String(info?.agreementDateRaw || unit.saleAgreementDate || '').trim(),
          agreementDate: formatDDMMYYYY(info?.agreementDateRaw || unit.saleAgreementDate),
          kycType: info?.kyc?.type || '—',
          kycNo: info?.kyc?.no || '—',
          unitAmount,
          loanToDeveloperAmount: Math.max(0, Number(unit.loanToDeveloperAmount || 0) || 0),
          receivedAmount,
          pendingAmount: getBookingPendingAmount(unitAmount, receivedAmount),
          gstStatus: bookingGstStatusByUnitId.get(unitId) || 'pending',
          saleDeedNumber: String(info?.saleDeedNumber || '').trim(),
          saleDeedDateRaw: String(info?.saleDeedDateRaw || '').trim(),
          saleDeedDate: formatDDMMYYYY(info?.saleDeedDateRaw || ''),
          saleDeedDocumentId: String(info?.saleDeedDocumentId || '').trim(),
        };
      })
      .filter((row) => row.receivedAmount > 0);
  }, [bookingGstStatusByUnitId, bookingInfoByUnitId, bookingMisReceiptAmountByUnitIdForTable, units]);

  const filteredRows = useMemo(() => {
    const loanFilteredRows = bookingLoanOnly
      ? rows.filter((row) => Number(row.loanToDeveloperAmount || 0) > 0)
      : rows;
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return loanFilteredRows;
    return loanFilteredRows.filter((row) =>
      [row.unitNo, row.names, row.agreementDate, row.kycType, row.kycNo].some((value) =>
        String(value || '').toLowerCase().includes(needle)
      )
    );
  }, [bookingLoanOnly, rows, search]);

  const contactRows = useMemo(() => {
    return units
      .filter(
        (unit) =>
          !!unit.latestBookingFormDate ||
          !!unit.bookingFormDate ||
          !!String(unit.activeBookingId || '').trim()
      )
      .map((unit) => {
        const unitId = String(unit.id || '').trim();
        const info = bookingInfoByUnitId.get(unitId);
        const name = String(
          info?.primaryName || unit.primaryCustomerName || unit.customerName || unit.allotteeName || ''
        ).trim();
        const mobile = String(
          info?.primaryMobile ||
            unit.primaryMobile ||
            unit.primaryCustomerMobile ||
            unit.mobileNumber ||
            unit.customerMobile ||
            ''
        ).trim();
        return {
          unitId,
          unitNo: String(unit.unitNumber || '').trim(),
          building: resolveUnitBuilding(String(unit.unitNumber || '').trim()),
          usage: String(unit.usage || '').trim() || 'Other',
          name,
          mobile,
        };
      })
      .filter((row) => row.unitNo)
      .sort((a, b) => compareUnitNumbers(a.unitNo, b.unitNo));
  }, [bookingInfoByUnitId, units]);

  const contactBuildingOptions = useMemo(() => {
    return Array.from(new Set(contactRows.map((row) => row.building).filter(Boolean))).sort((a, b) =>
      compareUnitNumbers(`${a}-1`, `${b}-1`)
    );
  }, [contactRows]);

  const contactUsageOptions = useMemo(() => {
    const scopedRows =
      contactBuildingFilter === 'all'
        ? contactRows
        : contactRows.filter((row) => row.building === contactBuildingFilter);
    return Array.from(new Set(scopedRows.map((row) => row.usage).filter(Boolean))).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [contactBuildingFilter, contactRows]);

  useEffect(() => {
    if (contactUsageFilter !== 'all' && !contactUsageOptions.includes(contactUsageFilter)) {
      setContactUsageFilter('all');
    }
  }, [contactUsageFilter, contactUsageOptions]);

  const filteredContactRows = useMemo(() => {
    const needle = String(search || '').trim().toLowerCase();
    return contactRows.filter((row) =>
      (contactBuildingFilter === 'all' || row.building === contactBuildingFilter) &&
      (contactUsageFilter === 'all' || row.usage === contactUsageFilter) &&
      (!needle ||
        [row.unitNo, row.name, row.mobile, row.building, row.usage].some((value) =>
          String(value || '').toLowerCase().includes(needle)
        ))
    );
  }, [contactBuildingFilter, contactRows, contactUsageFilter, search]);

  const exportTillDate = useMemo(() => parseDateLike(exportTillDateYMD), [exportTillDateYMD]);

  const reraRows = useMemo(() => {
    const getExclusiveArea = (unit: UnitRow): number => {
      const wash =
        Number(
          unit.exclusiveWashArea ??
            unit.area_of_exclusive_wash ??
            (unit.customFields as any)?.area_of_exclusive_wash ??
            0
        ) || 0;
      const balcony = Number(unit.exclusiveBalconyArea || 0) || 0;
      const terrace =
        Number(
          unit.openTerraceArea ??
            unit.area_of_exclusive_open_terrace ??
            (unit.customFields as any)?.area_of_exclusive_open_terrace ??
            0
        ) || 0;
      return wash + balcony + terrace;
    };
    return units.map((unit) => {
      const unitId = String(unit.id || '').trim();
      const info = bookingInfoByUnitId.get(unitId);
      const hasActiveBooking = !!String(unit.activeBookingId || '').trim();
      const activeReceiptAmount = Number(bookingReceiptAmountByUnitId.get(unitId) || 0);
      const isBookedByReceipt = hasActiveBooking && activeReceiptAmount > 0;
      const receivedAmount = isBookedByReceipt ? activeReceiptAmount : 0;
      return {
        unitId,
        blockType: String(unit.blockType || '').trim() || '—',
        flatNo: String(unit.unitNumber || '—').trim() || '—',
        usage: String(unit.usage || '').trim() || '—',
        carpetArea: Number(unit.carpetArea || 0) || 0,
        areaExclusive: getExclusiveArea(unit),
        status: isBookedByReceipt ? 'BOOKED' : 'UNBOOKED',
        unitAmount: Number(unit.unitAmount || 0) || 0,
        receivedAmount,
        agreementDateRaw: isBookedByReceipt ? String(info?.agreementDateRaw || unit.saleAgreementDate || '').trim() : '',
        agreementDate: isBookedByReceipt ? formatDDMMYYYY(info?.agreementDateRaw || unit.saleAgreementDate) : '—',
        encumbranceStatus: String(unit.encumbranceStatus || '').trim() || 'No Encumbrance',
        allotteeNames: isBookedByReceipt
          ? (info?.names || []).map((name) => String(name || '').trim()).filter(Boolean)
          : [],
        allotteeName: isBookedByReceipt ? joinNamesWithAnd(info?.names || []) : '—',
        kycType: isBookedByReceipt ? String(info?.kyc?.type || '—').trim() || '—' : '—',
        kycNo: isBookedByReceipt ? String(info?.kyc?.no || '—').trim() || '—' : '—',
        mobileNumber: isBookedByReceipt ? String(info?.primaryMobile || '').trim() || '—' : '—',
        redevelopment: String(unit.redevelopment || '').trim() || 'NO',
      };
    });
  }, [bookingInfoByUnitId, bookingReceiptAmountByUnitId, units]);

  const filteredReraRows = useMemo(() => {
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return reraRows;
    return reraRows.filter((row) =>
      [
        row.blockType,
        row.flatNo,
        row.usage,
        row.status,
        row.encumbranceStatus,
        row.allotteeName,
        row.kycType,
        row.kycNo,
        row.mobileNumber,
        row.redevelopment,
        row.agreementDate,
      ].some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [reraRows, search]);
  const shownReraRows = useMemo(() => {
    if (!exportTillDate) return filteredReraRows;
    return filteredReraRows.filter((row: any) => {
      const agreementDate = parseDateLike(row?.agreementDateRaw || row?.agreementDate);
      if (!agreementDate) return true;
      return agreementDate.getTime() <= exportTillDate.getTime();
    });
  }, [exportTillDate, filteredReraRows]);

  const gstFinancialYearOptions = useMemo(() => {
    const keys = new Set<string>();
    const currentFy = getCurrentFinancialYearKey();
    keys.add(currentFy);
    const currentStartYear = Number(currentFy.slice(0, 4));
    if (Number.isFinite(currentStartYear)) {
      for (let offset = -4; offset <= 2; offset += 1) {
        const startYear = currentStartYear + offset;
        keys.add(`${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`);
      }
    }
    bankTxns.forEach((txn) => {
      if (!isCustomerReceiptTxn(txn) && String(txn.receiptCategory || '').toLowerCase().trim() !== 'gst') return;
      const txnDate = resolveTxnDateLike(txn);
      if (txnDate) keys.add(getFinancialYearKey(txnDate));
    });
    return Array.from(keys).sort((a, b) => b.localeCompare(a));
  }, [bankTxns]);

  const gstRows = useMemo(() => {
    const monthRaw = gstMonthMode === 'month' ? String(gstMonthYMD || '').trim().toLowerCase() : '';
    const financialYearRaw =
      gstMonthMode === 'financial_year' ? String(gstFinancialYear || '').trim().toLowerCase() : '';
    const unitIdByNumber = new Map<string, string>();
    const activeBookingByUnitId = new Map<string, string>();
    const activeBookingStartByUnitId = new Map<string, number>();
    units.forEach((unit) => {
      const unitId = String(unit.id || '').trim();
      const unitNo = String(unit.unitNumber || '').trim().toLowerCase();
      if (unitId && unitNo && !unitIdByNumber.has(unitNo)) unitIdByNumber.set(unitNo, unitId);
      const activeBookingId = String(unit.activeBookingId || '').trim();
      if (unitId && activeBookingId) activeBookingByUnitId.set(unitId, activeBookingId);
      const bookingCreatedAtMs = Number(bookingInfoByUnitId.get(unitId)?.bookingCreatedAtMs || 0);
      if (unitId && bookingCreatedAtMs > 0) activeBookingStartByUnitId.set(unitId, bookingCreatedAtMs);
    });

    const firstCustomerReceiptDateByUnitId = new Map<string, any>();
    const gstPaidByUnitId = new Map<string, number>();

    bankTxns.forEach((txn) => {
      const txnType = String(txn.txnType || '').toLowerCase();
      if (txnType !== 'receipt') return;
      const unitIdRaw = String(txn.unitId || txn.unitID || '').trim();
      const unitNoRaw = String(txn.unitNumber || txn.unitNo || txn.unit_no || '').trim().toLowerCase();
      const unitId = unitIdRaw || unitIdByNumber.get(unitNoRaw) || '';
      if (!unitId) return;
      const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
      if (!activeBookingId) return;
      const txnBookingId = String(txn.bookingId || '').trim();
      if (txnBookingId && txnBookingId !== activeBookingId) return;
      const txnDateObj = resolveTxnDateLike(txn);
      if (!txnDateObj) return;
      if (!txnBookingId) {
        const bookingHistoryCount = Number(bookingInfoByUnitId.get(unitId)?.bookingHistoryCount || 0);
        const hasSingleKnownBooking = bookingHistoryCount === 1;
        if (!hasSingleKnownBooking) {
          const activeBookingStartMs = Number(activeBookingStartByUnitId.get(unitId) || 0);
          if (activeBookingStartMs > 0 && txnDateObj.getTime() < activeBookingStartMs) return;
        }
      }
      const amount = Number(txn.amount || 0);
      if (!Number.isFinite(amount) || amount <= 0) return;
      const category = String(txn.receiptCategory || (txn as any)?.receiptFor || '').toLowerCase().trim();
      const isGstReceipt = category === 'gst';
      const isCustomerReceipt =
        !category ||
        category === 'customer' ||
        category === 'dp' ||
        category === 'downpayment' ||
        category === 'down_payment' ||
        category === 'booking';
      if (isCustomerReceipt) {
        const prev = firstCustomerReceiptDateByUnitId.get(unitId);
        if (!prev) {
          firstCustomerReceiptDateByUnitId.set(unitId, txnDateObj);
        } else {
          const prevDate = parseDateLike(prev);
          const currDate = parseDateLike(txnDateObj);
          if (currDate && (!prevDate || currDate.getTime() < prevDate.getTime())) {
            firstCustomerReceiptDateByUnitId.set(unitId, txnDateObj);
          }
        }
      }
      if (isGstReceipt) {
        gstPaidByUnitId.set(unitId, Number(gstPaidByUnitId.get(unitId) || 0) + amount);
      }
    });

    return units
      .map((unit) => {
        const unitId = String(unit.id || '').trim();
        const activeBookingId = String((unit as any)?.activeBookingId || '').trim();
        const activeReceiptAmount = Number(bookingReceiptAmountByUnitId.get(unitId) || 0);
        const includeInGstReport = !!activeBookingId && activeReceiptAmount > 0;
        if (!includeInGstReport) return null;

        const firstReceiptRaw = firstCustomerReceiptDateByUnitId.get(unitId);
        if (!firstReceiptRaw) return null;
        const firstReceiptDate = parseDateLike(firstReceiptRaw);
        if (!firstReceiptDate) return null;
        const firstReceiptMonth = `${firstReceiptDate.getFullYear()}-${String(firstReceiptDate.getMonth() + 1).padStart(
          2,
          '0'
        )}`;
        const firstReceiptFinancialYear = getFinancialYearKey(firstReceiptDate).toLowerCase();
        if (monthRaw && firstReceiptMonth !== monthRaw) return null;
        if (financialYearRaw && firstReceiptFinancialYear !== financialYearRaw) return null;

        const info = bookingInfoByUnitId.get(unitId);
        const explicitGst =
          Number((unit as any)?.gst ?? (unit as any)?.gstAmount ?? (unit as any)?.gst_amount ?? NaN);
        const computedGst = computeGstAmountForUnit(
          Number(unit.unitAmount || 0),
          unit as any,
          (projectMeta as any) || null,
          1
        );
        const gstAmount = Number.isFinite(explicitGst) && explicitGst > 0 ? explicitGst : computedGst;
        const unitAmount = Number(unit.unitAmount || 0) || 0;
        const gstPercent = unitAmount > 0 && gstAmount > 0 ? (gstAmount / unitAmount) * 100 : 0;
        const gstPaid = Number(gstPaidByUnitId.get(unitId) || 0);
        const balance = Math.max(0, gstAmount - gstPaid);
        const status = gstAmount > 0 && gstPaid >= gstAmount ? 'PAID' : 'PENDING';

        return {
          unitId,
          unitNo: String(unit.unitNumber || '—').trim() || '—',
          allotteeName: joinNamesWithAnd(info?.names || []),
          firstReceiptDate: formatDDMMYYYY(firstReceiptRaw),
          dueMonth: `${String(firstReceiptDate.getMonth() + 1).padStart(2, '0')}-${firstReceiptDate.getFullYear()}`,
          unitAmount,
          gstPercent,
          gstAmount,
          gstPaid,
          balance,
          status,
        };
      })
      .filter(Boolean) as Array<{
      unitId: string;
      unitNo: string;
      allotteeName: string;
      firstReceiptDate: string;
      dueMonth: string;
      unitAmount: number;
      gstPercent: number;
      gstAmount: number;
      gstPaid: number;
      balance: number;
      status: 'PAID' | 'PENDING';
    }>;
  }, [
    bankTxns,
    bookingInfoByUnitId,
    bookingReceiptAmountByUnitId,
    gstFinancialYear,
    gstMonthMode,
    gstMonthYMD,
    projectMeta,
    units,
  ]);

  const filteredGstRows = useMemo(() => {
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return gstRows;
    return gstRows.filter((row) =>
      [row.unitNo, row.allotteeName, row.firstReceiptDate, row.dueMonth, row.status].some((value) =>
        String(value || '').toLowerCase().includes(needle)
      )
    );
  }, [gstRows, search]);
  const shownGstRows = useMemo(() => {
    if (gstTopFilter === 'paid') return filteredGstRows.filter((row) => Number(row.gstPaid || 0) > 0);
    if (gstTopFilter === 'balance') return filteredGstRows.filter((row) => Number(row.balance || 0) > 0);
    if (gstTopFilter === 'amount') return filteredGstRows.filter((row) => Number(row.gstAmount || 0) > 0);
    return filteredGstRows;
  }, [filteredGstRows, gstTopFilter]);

  const shownUnitsCount = filteredBaseRows.length;
  const shownReceivedAmount = useMemo(
    () => filteredBaseRows.reduce((sum, row) => sum + Number(row.receivedAmount || 0), 0),
    [filteredBaseRows]
  );
  const shownSoldAmount = useMemo(
    () => filteredBaseRows.reduce((sum, row) => sum + Number(row.unitAmount || 0), 0),
    [filteredBaseRows]
  );
  const shownReraUnitsCount = shownReraRows.length;
  const shownReraReceivedAmount = useMemo(
    () => shownReraRows.reduce((sum, row) => sum + Number(row.receivedAmount || 0), 0),
    [shownReraRows]
  );
  const bookingTrend = useMemo(() => {
    const tillDate = parseDateLike(exportTillDateYMD);
    if (!tillDate) return { points: [] as Array<{ label: string; fullLabel: string; value: number; cutoffMs: number }>, mode: 'day' as const };
    const tillEndMs = new Date(
      tillDate.getFullYear(),
      tillDate.getMonth(),
      tillDate.getDate(),
      23,
      59,
      59,
      999
    ).getTime();
    const visibleUnitIds = new Set(
      units
        .filter((unit) => !bookingLoanOnly || Number(unit.loanToDeveloperAmount || 0) > 0)
        .map((unit) => String(unit.id || '').trim())
        .filter(Boolean)
    );
    const customerTxns = bankTxns
      .filter((txn) => isCustomerReceiptTxn(txn) || isCustomerRefundTxn(txn))
      .filter((txn) => !isExcludedFromBookingReport(txn))
      .filter((txn) => {
        const unitId = String(txn.unitId || '').trim();
        return !!unitId && visibleUnitIds.has(unitId);
      })
      .map((txn) => {
        const amount = Number(txn.amount || 0);
        const signedAmount = isCustomerRefundTxn(txn) ? -amount : amount;
        return { date: resolveTxnDateLike(txn), amount: signedAmount };
      })
      .filter((txn) => txn.date && Number.isFinite(txn.amount) && txn.amount !== 0)
      .map((txn) => ({ date: txn.date as Date, amount: txn.amount }))
      .filter((txn) => txn.date.getTime() <= tillEndMs);
    if (!customerTxns.length) return { points: [] as Array<{ label: string; fullLabel: string; value: number; cutoffMs: number }>, mode: 'day' as const };

    const firstDate = new Date(
      Math.min(...customerTxns.map((txn) => new Date(txn.date.getFullYear(), txn.date.getMonth(), txn.date.getDate()).getTime()))
    );
    const spanDays = Math.max(1, Math.floor((tillEndMs - firstDate.getTime()) / 86400000) + 1);
    const mode: 'day' | 'month' = spanDays > 180 ? 'month' : 'day';

    if (mode === 'day') {
      const amountByDay = new Map<string, number>();
      customerTxns.forEach((txn) => {
        const key = toYMD(txn.date);
        amountByDay.set(key, Number(amountByDay.get(key) || 0) + txn.amount);
      });
      const points: Array<{ label: string; fullLabel: string; value: number; cutoffMs: number }> = [];
      let running = 0;
      const cursor = new Date(firstDate.getFullYear(), firstDate.getMonth(), firstDate.getDate());
      const end = new Date(tillDate.getFullYear(), tillDate.getMonth(), tillDate.getDate());
      while (cursor.getTime() <= end.getTime()) {
        const key = toYMD(cursor);
        running += Number(amountByDay.get(key) || 0);
        points.push({
          label: formatAxisLabel(cursor, 'day'),
          fullLabel: formatAxisFullLabel(cursor, 'day'),
          value: running,
          cutoffMs: new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 23, 59, 59, 999).getTime(),
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      return { points, mode };
    }

    const amountByMonth = new Map<string, number>();
    customerTxns.forEach((txn) => {
      const key = toYM(txn.date);
      amountByMonth.set(key, Number(amountByMonth.get(key) || 0) + txn.amount);
    });
    const points: Array<{ label: string; fullLabel: string; value: number; cutoffMs: number }> = [];
    let running = 0;
    const cursor = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
    const end = new Date(tillDate.getFullYear(), tillDate.getMonth(), 1);
    while (cursor.getTime() <= end.getTime()) {
      const key = toYM(cursor);
      running += Number(amountByMonth.get(key) || 0);
      points.push({
        label: formatAxisLabel(cursor, 'month'),
        fullLabel: formatAxisFullLabel(cursor, 'month'),
        value: running,
        cutoffMs: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999).getTime(),
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return { points, mode };
  }, [bankTxns, bookingLoanOnly, exportTillDateYMD, units]);
  const bookingTrendSvg = useMemo(() => {
    const points = bookingTrend.points || [];
    if (!points.length) return null;
    const width = 1000;
    const height = 110;
    const paddingX = 8;
    const paddingTop = 8;
    const paddingBottom = 18;
    const drawableWidth = width - paddingX * 2;
    const drawableHeight = height - paddingTop - paddingBottom;
    const unitMap = new Map<string, UnitRow>();
    units
      .filter((unit) => !bookingLoanOnly || Number(unit.loanToDeveloperAmount || 0) > 0)
      .forEach((unit) => {
      const unitId = String(unit.id || '').trim();
      if (unitId) unitMap.set(unitId, unit);
    });
    const firstReceiptMsByUnitId = new Map<string, number>();
    bankTxns.forEach((txn) => {
      if (!isCustomerReceiptTxn(txn)) return;
      const unitId = String(txn.unitId || '').trim();
      if (!unitId || !unitMap.has(unitId)) return;
      const txnDate = resolveTxnDateLike(txn);
      const txnMs = txnDate ? txnDate.getTime() : 0;
      if (!txnMs) return;
      const amount = Number(txn.amount || 0);
      if (!Number.isFinite(amount) || amount <= 0) return;
      const prev = Number(firstReceiptMsByUnitId.get(unitId) || 0);
      if (!prev || txnMs < prev) firstReceiptMsByUnitId.set(unitId, txnMs);
    });
    const soldEvents = Array.from(firstReceiptMsByUnitId.entries())
      .map(([unitId, ms]) => {
        const unitAmount = Number(unitMap.get(unitId)?.unitAmount || 0);
        if (!Number.isFinite(unitAmount) || unitAmount <= 0) return null;
        return { ms, unitAmount };
      })
      .filter(Boolean)
      .sort((a, b) => Number((a as any).ms || 0) - Number((b as any).ms || 0)) as Array<{ ms: number; unitAmount: number }>;

    const baseCoords = points.map((point, idx) => {
      const x = points.length === 1 ? width / 2 : paddingX + (idx / (points.length - 1)) * drawableWidth;
      return {
        x,
        receivedValue: Number(point.value || 0),
        label: point.label,
        fullLabel: point.fullLabel,
        cutoffMs: Number(point.cutoffMs || 0),
      };
    });
    let soldCursor = 0;
    let soldRunning = 0;
    const coords = baseCoords.map((coord) => {
      while (soldCursor < soldEvents.length && Number(soldEvents[soldCursor]?.ms || 0) <= coord.cutoffMs) {
        soldRunning += Number(soldEvents[soldCursor]?.unitAmount || 0);
        soldCursor += 1;
      }
      return { ...coord, soldValue: soldRunning };
    });
    const maxValue = Math.max(
      ...coords.map((coord) => Math.max(Number(coord.receivedValue || 0), Number(coord.soldValue || 0))),
      1
    );
    const coordsWithY = coords.map((coord) => ({
      ...coord,
      yReceived: paddingTop + (1 - Number(coord.receivedValue || 0) / maxValue) * drawableHeight,
      ySold: paddingTop + (1 - Number(coord.soldValue || 0) / maxValue) * drawableHeight,
    }));
    const maxTickCount = Math.max(2, Math.floor(width / 95));
    const step = Math.max(1, Math.ceil((coordsWithY.length - 1) / Math.max(1, maxTickCount - 1)));
    const tickIndices = Array.from(
      new Set([0, ...coordsWithY.map((_, idx) => idx).filter((idx) => idx % step === 0), coordsWithY.length - 1])
    ).filter((idx) => idx >= 0 && idx < coordsWithY.length);
    const receivedPath = coordsWithY.map((coord) => `${coord.x.toFixed(2)},${coord.yReceived.toFixed(2)}`).join(' ');
    const soldPath = coordsWithY.map((coord) => `${coord.x.toFixed(2)},${coord.ySold.toFixed(2)}`).join(' ');
    return { width, height, receivedPath, soldPath, maxValue, coords: coordsWithY, tickIndices };
  }, [bankTxns, bookingLoanOnly, bookingTrend.points, units]);
  useEffect(() => {
    if (!bookingTrend.points.length) {
      setBookingTrendHoverIndex(null);
      setBookingTrendSelectedIndex(null);
      setSelectedBookingTrendCutoffMs(null);
      return;
    }
    setBookingTrendHoverIndex(bookingTrend.points.length - 1);
    setBookingTrendSelectedIndex(null);
    setSelectedBookingTrendCutoffMs(null);
  }, [bookingTrend.points]);
  const exportRows = useMemo(() => filteredBaseRows, [filteredBaseRows]);
  const bookingMisExportSections = useMemo(() => {
    const advanceRows: typeof exportRows = [];
    const saleDeedRows: typeof exportRows = [];
    exportRows.forEach((row: any) => {
      const hasSaleDeed =
        !!String(row?.saleDeedNumber || '').trim() ||
        !!String(row?.saleDeedDateRaw || '').trim() ||
        !!String(row?.saleDeedDocumentId || '').trim();
      if (hasSaleDeed) saleDeedRows.push(row);
      else advanceRows.push(row);
    });
    return { advanceRows, saleDeedRows };
  }, [exportRows]);

  const exportReraRows = useMemo(() => shownReraRows, [shownReraRows]);

  const gstSummary = useMemo(() => {
    const unitsCount = shownGstRows.length;
    const totalGst = shownGstRows.reduce((sum, row) => sum + Number(row.gstAmount || 0), 0);
    const totalPaid = shownGstRows.reduce((sum, row) => sum + Number(row.gstPaid || 0), 0);
    const totalBalance = shownGstRows.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    return { unitsCount, totalGst, totalPaid, totalBalance };
  }, [shownGstRows]);

  const exportBookingMisCsv = () => {
    const includeKycType = bookingExportColumns.kycType;
    const includeKycNo = bookingExportColumns.kycNo;
    const advanceHeader = [
      'Unit No',
      'Name',
      'Date of Agreement',
      ...(includeKycType ? ['KYC Type'] : []),
      ...(includeKycNo ? ['KYC No'] : []),
      'Received Amount',
    ];
    const saleDeedHeader = [
      'Unit No',
      'Name',
      'Date of Agreement',
      ...(includeKycType ? ['KYC Type'] : []),
      ...(includeKycNo ? ['KYC No'] : []),
      'Received Amount',
      'Sale Deed No',
      'Sale Deed Date',
    ];
    const escapeCsv = (value: any) => {
      const text = String(value ?? '');
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const buildSection = (
      title: string,
      sectionRows: typeof exportRows,
      header: string[],
      includeSaleDeedColumns = false
    ) => {
      const total = sectionRows.reduce((sum, row: any) => sum + Number(row.receivedAmount || 0), 0);
      return [
        escapeCsv(`${title} (${sectionRows.length} units, Total ₹ ${formatAmount2(total)})`),
        header.join(','),
        ...sectionRows.map((row: any) =>
          [
            escapeCsv(row.unitNo),
            escapeCsv(row.names),
            escapeCsv(row.agreementDate === '—' ? '' : row.agreementDate),
            ...(includeKycType ? [escapeCsv(row.kycType === '—' ? '' : row.kycType)] : []),
            ...(includeKycNo ? [escapeCsv(row.kycNo === '—' ? '' : row.kycNo)] : []),
            escapeCsv(formatAmount2(row.receivedAmount)),
            ...(includeSaleDeedColumns
              ? [
                  escapeCsv(row.saleDeedNumber || ''),
                  escapeCsv(row.saleDeedDate === '—' ? '' : row.saleDeedDate),
                ]
              : []),
          ].join(',')
        ),
        [
          'Total',
          ...Array(Math.max(0, header.length - 2 - (includeSaleDeedColumns ? 2 : 0))).fill(''),
          escapeCsv(formatAmount2(total)),
          ...(includeSaleDeedColumns ? ['', ''] : []),
        ].join(','),
      ];
    };
    const csv = [
      ...buildSection('Advance Booking', bookingMisExportSections.advanceRows, advanceHeader),
      '',
      ...buildSection('Sale Deed', bookingMisExportSections.saleDeedRows, saleDeedHeader, true),
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scope =
      bookingPeriodMode === 'financial_year'
        ? `fy_${bookingFinancialYear}`
        : `till_${exportTillDateYMD || new Date().toISOString().slice(0, 10)}`;
    a.download = `booking_mis_${scope}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportDialogOpen(false);
  };

  const exportReraBookingXlsx = () => {
    const header = [
      'BLOCK TYPE',
      'FLAT/BUNGLO W/OFFICE',
      'USAGE',
      'CARPET AREA',
      'AREA OF EXCLUSIVE',
      'STATUS',
      'UNIT AMOUNT',
      'RECEIVED AMOUNT',
      'DATE OF AGREEMENT OF SALE',
      'ENCUMBRANCE STATUS',
      'ALLOTEE NAME',
      'TYPE OF KYC',
      'KYC ID',
      'MOBILE NUMBER',
      'REDEVELOPMENT',
    ];
    const toAmountNumber = (value: any) => Math.round(Number(value || 0));
    const rows = exportReraRows.map((row) => [
      row.blockType === '—' ? '' : row.blockType,
      row.flatNo === '—' ? '' : row.flatNo,
      row.usage === '—' ? '' : row.usage,
      Number(Number(row.carpetArea || 0).toFixed(2)),
      Number(Number(row.areaExclusive || 0).toFixed(2)),
      row.status === '—' ? '' : row.status,
      toAmountNumber(row.unitAmount),
      toAmountNumber(row.receivedAmount),
      row.agreementDate === '—' ? '' : formatDDMMYYYY(row.agreementDateRaw || row.agreementDate),
      row.encumbranceStatus === '—' ? '' : row.encumbranceStatus,
      row.allotteeName === '—' ? '' : row.allotteeName,
      row.kycType === '—' ? '' : row.kycType,
      row.kycNo === '—' ? '' : row.kycNo,
      row.mobileNumber === '—' ? '' : row.mobileNumber,
      row.redevelopment === '—' ? '' : row.redevelopment,
    ]);
    const blob = createXlsxBlob('RERA Booking Report', header, rows);
    downloadBlob(blob, `rera_booking_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportDialogOpen(false);
  };

  const exportGstCsv = () => {
    const header = [
      'Unit No',
      'Allottee Name',
      'First Receipt Date',
      'GST Due Month',
      'Unit Amount',
      'GST %',
      'GST Amount',
      'GST Paid',
    ];
    const escapeCsv = (value: any) => {
      const text = String(value ?? '');
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const amountText = (value: number) => Number(value || 0).toFixed(2);
    const csv = [
      header.join(','),
      ...shownGstRows.map((row) =>
        [
          escapeCsv(row.unitNo),
          escapeCsv(row.allotteeName === '—' ? '' : row.allotteeName),
          escapeCsv(row.firstReceiptDate),
          escapeCsv(row.dueMonth),
          escapeCsv(amountText(row.unitAmount)),
          escapeCsv(Number(row.gstPercent || 0).toFixed(2)),
          escapeCsv(amountText(row.gstAmount)),
          escapeCsv(amountText(row.gstPaid)),
        ].join(',')
      ),
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scope =
      gstMonthMode === 'month'
        ? gstMonthYMD
        : gstMonthMode === 'financial_year'
          ? `fy_${gstFinancialYear}`
          : 'all';
    a.download = `gst_report_${scope}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportDialogOpen(false);
  };

  const exportContactListCsv = () => {
    const header = ['Unit Number', 'Name', 'Mobile Number'];
    const escapeCsv = (value: any) => {
      const text = String(value ?? '');
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const csv = [
      header.join(','),
      ...filteredContactRows.map((row) =>
        [escapeCsv(row.unitNo), escapeCsv(row.name), escapeCsv(row.mobile)].join(',')
      ),
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unit_contact_list_${String(projectMeta?.name || currentProjectId || 'project').replace(/\s+/g, '_')}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportDialogOpen(false);
  };

  const blobFromRenderTemplateResult = (resultData: unknown): Blob => {
    const data = (resultData || {}) as {
      pdfBytes?: number[] | Record<string, number>;
      pdfBase64?: string | { type?: string; data?: number[] };
      mimeType?: string;
    };
    const mimeType = data.mimeType || 'application/pdf';
    const rawBytes = Array.isArray(data.pdfBytes)
      ? data.pdfBytes
      : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
    if (rawBytes && rawBytes.length > 0) {
      return new Blob([new Uint8Array(rawBytes)], { type: mimeType });
    }
    const rawBase64 =
      typeof data.pdfBase64 === 'string'
        ? data.pdfBase64
        : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
        ? (() => {
            const source = data.pdfBase64?.data || [];
            let binaryBuffer = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < source.length; i += chunkSize) {
              const chunk = source.slice(i, i + chunkSize);
              binaryBuffer += String.fromCharCode(...chunk);
            }
            return btoa(binaryBuffer);
          })()
        : '';
    if (!rawBase64) throw new Error('PDF response was empty.');
    const normalizedBase64 = String(rawBase64)
      .replace(/^data:application\/pdf;base64,/i, '')
      .replace(/\s+/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedBase64 = normalizedBase64.padEnd(
      normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4),
      '='
    );
    const binary = atob(paddedBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mimeType });
  };

  const exportBookingMisPdf = async () => {
    if (exportPdfBusy) return;
    setExportPdfError(null);
    setExportPdfBusy(true);
    try {
      const periodLabel =
        bookingPeriodMode === 'financial_year'
          ? `Financial Year: ${bookingFinancialYear}`
          : `Till Date: ${exportTillDate ? formatDDMMYYYY(exportTillDate) : '—'}`;
      const fileScope =
        bookingPeriodMode === 'financial_year'
          ? `fy_${bookingFinancialYear}`
          : `till_${exportTillDateYMD || 'till_date'}`;
      const totalAmount = exportRows.reduce((sum, row: any) => sum + Number(row.receivedAmount || 0), 0);
      const totalUnitAmount = exportRows.reduce((sum, row: any) => sum + Number(row.unitAmount || 0), 0);
      const totalPendingAmount = exportRows.reduce((sum, row: any) => sum + Number(row.pendingAmount || 0), 0);
      const showTotalAmount = bookingColumns.totalAmount;
      const showPendingAmount = bookingColumns.pendingAmount;
      const showGstStatus = bookingColumns.gstStatus;
      const includeKycType = bookingExportColumns.kycType;
      const includeKycNo = bookingExportColumns.kycNo;
      const safe = (value: any) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      const renderBookingMisTable = (
        title: string,
        sectionRows: typeof exportRows,
        includeSaleDeedColumns = false
      ) => {
        const sectionTotal = sectionRows.reduce((sum, row: any) => sum + Number(row.receivedAmount || 0), 0);
        const sectionUnitTotal = sectionRows.reduce((sum, row: any) => sum + Number(row.unitAmount || 0), 0);
        const sectionPendingTotal = sectionRows.reduce((sum, row: any) => sum + Number(row.pendingAmount || 0), 0);
        const columnCount = 5 + Number(includeKycType) + Number(includeKycNo) + Number(showTotalAmount) + Number(showPendingAmount) + Number(showGstStatus) + (includeSaleDeedColumns ? 2 : 0);
        const tableRowsHtml = sectionRows
          .map((row: any, idx: number) => {
          const namesHtml = String(row.names || '')
            .split(',')
            .map((name: string) => safe(name).trim())
            .filter(Boolean)
            .join('<br/>');
          return `
            <tr>
              <td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">${idx + 1}</td>
              <td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;">${safe(row.unitNo)}</td>
              <td style="border:1px solid #bbb;padding:6px 8px;">${namesHtml || ''}</td>
              <td style="border:1px solid #bbb;padding:6px 8px;">${safe(row.agreementDate === '—' ? '' : row.agreementDate)}</td>
              ${includeKycType ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">${safe(row.kycType === '—' ? '' : row.kycType)}</td>` : ''}
              ${includeKycNo ? `<td style="border:1px solid #bbb;padding:6px 8px;">${safe(row.kycNo === '—' ? '' : row.kycNo)}</td>` : ''}
              ${showTotalAmount ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹ ${formatAmount2(Number(row.unitAmount || 0))}</td>` : ''}
              <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹ ${formatAmount2(Number(row.receivedAmount || 0))}</td>
              ${showPendingAmount ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹ ${formatAmount2(Number(row.pendingAmount || 0))}</td>` : ''}
              ${showGstStatus ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:center;color:${row.gstStatus === 'paid' ? '#1b7f3d' : '#c62828'};font-weight:700;">${row.gstStatus === 'paid' ? '✓ Received' : '✕ Pending'}</td>` : ''}
              ${
                includeSaleDeedColumns
                  ? `<td style="border:1px solid #bbb;padding:6px 8px;">${safe(row.saleDeedNumber || '')}</td>
                     <td style="border:1px solid #bbb;padding:6px 8px;">${safe(row.saleDeedDate === '—' ? '' : row.saleDeedDate)}</td>`
                  : ''
              }
            </tr>
          `;
        })
        .join('');
        return `
          <div style="margin:${title === 'Advance Booking' ? '0' : '8mm'} 0 2mm 0;">
            <div style="font-size:14px;font-weight:800;margin-bottom:2mm;">${safe(title)}</div>
            <div style="font-size:11px;margin-bottom:2mm;">Units: ${sectionRows.length} &nbsp;|&nbsp; ${showTotalAmount ? `Total Amount: ₹ ${formatAmount2(sectionUnitTotal)} &nbsp;|&nbsp; ` : ''}Received Amount: ₹ ${formatAmount2(sectionTotal)}${showPendingAmount ? ` &nbsp;|&nbsp; Pending Amount: ₹ ${formatAmount2(sectionPendingTotal)}` : ''}</div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:${showTotalAmount || showPendingAmount || showGstStatus ? '9px' : '11px'};table-layout:auto;">
            <thead>
              <tr style="background:#f2f4f7;">
                <th style="border:1px solid #bbb;padding:6px 8px;width:28px;">#</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Unit No</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Name</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Date of Agreement</th>
                ${includeKycType ? '<th style="border:1px solid #bbb;padding:6px 8px;">KYC Type</th>' : ''}
                ${includeKycNo ? '<th style="border:1px solid #bbb;padding:6px 8px;">KYC No</th>' : ''}
                ${showTotalAmount ? '<th style="border:1px solid #bbb;padding:6px 8px;text-align:right;">Total Amount</th>' : ''}
                <th style="border:1px solid #bbb;padding:6px 8px;text-align:right;">Received Amount</th>
                ${showPendingAmount ? '<th style="border:1px solid #bbb;padding:6px 8px;text-align:right;">Pending Amount</th>' : ''}
                ${showGstStatus ? '<th style="border:1px solid #bbb;padding:6px 8px;text-align:center;">GST Status</th>' : ''}
                ${
                  includeSaleDeedColumns
                    ? '<th style="border:1px solid #bbb;padding:6px 8px;">Sale Deed No</th><th style="border:1px solid #bbb;padding:6px 8px;">Sale Deed Date</th>'
                    : ''
                }
              </tr>
            </thead>
            <tbody>
              ${
                tableRowsHtml ||
                `<tr><td colspan="${columnCount}" style="border:1px solid #bbb;padding:10px;text-align:center;">No rows for selected date.</td></tr>`
              }
              <tr>
                <td colspan="${4 + Number(includeKycType) + Number(includeKycNo)}" style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">Total</td>
                ${showTotalAmount ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹ ${formatAmount2(sectionUnitTotal)}</td>` : ''}
                <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹ ${formatAmount2(sectionTotal)}</td>
                ${showPendingAmount ? `<td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹ ${formatAmount2(sectionPendingTotal)}</td>` : ''}
                ${showGstStatus ? '<td style="border:1px solid #bbb;padding:6px 8px;"></td>' : ''}
                ${includeSaleDeedColumns ? '<td style="border:1px solid #bbb;padding:6px 8px;"></td><td style="border:1px solid #bbb;padding:6px 8px;"></td>' : ''}
              </tr>
            </tbody>
          </table>
        `;
      };
      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;color:#111;">
          <div style="margin-bottom:8px;">
            <div style="font-size:20px;font-weight:800;">Booking MIS</div>
            <div style="font-size:12px;margin-top:2px;">${safe(periodLabel)}</div>
            <div style="font-size:12px;">Units: ${exportRows.length} &nbsp;|&nbsp; ${showTotalAmount ? `Total Amount: ₹ ${formatAmount2(totalUnitAmount)} &nbsp;|&nbsp; ` : ''}Received Amount: ₹ ${formatAmount2(totalAmount)}${showPendingAmount ? ` &nbsp;|&nbsp; Pending Amount: ₹ ${formatAmount2(totalPendingAmount)}` : ''}</div>
          </div>
          ${renderBookingMisTable('Advance Booking', bookingMisExportSections.advanceRows)}
          ${renderBookingMisTable('Sale Deed', bookingMisExportSections.saleDeedRows, true)}
        </div>
      `;
      const payload = buildPdfPayloadFromTemplate({
        html,
        tokenValues: {},
        visualModel: {
          showTitle: false,
          showRefNo: false,
          showDate: false,
          showPageNumbers: true,
          backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
          backgroundOpacity: Number(projectMeta?.backgroundOpacity || 0.14),
          backgroundSize: 'contain',
          backgroundScale: 70,
          backgroundPosX: 50,
          backgroundPosY: 50,
          companyLogoHeightCm: 1.25,
          projectLogoHeightCm: 1.25,
        },
        companyLogoUrl: String(companyMeta?.logoUrl || projectMeta?.logoUrl || ''),
        projectLogoUrl: String(projectMeta?.logoUrl || ''),
        companyAddress: String(companyMeta?.address || ''),
        companyMobile: String(companyMeta?.mobile || ''),
        companyEmail: String(companyMeta?.email || ''),
        companyFontFamily: String(companyMeta?.fontFamily || ''),
        companyFontUrl: String(companyMeta?.fontUrl || ''),
        companyFontFormat: String(companyMeta?.fontFormat || ''),
        gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
        paperSize: 'A4',
        fileName: `booking_mis_${String(projectMeta?.name || currentProjectId || 'project').replace(/\s+/g, '_')}_${fileScope}`,
      });
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf(payload);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking_mis_${String(projectMeta?.name || currentProjectId || 'project').replace(/\s+/g, '_')}_${fileScope}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (err: any) {
      setExportPdfError(err?.message || 'Failed to export PDF.');
    } finally {
      setExportPdfBusy(false);
    }
  };

  const exportGstPdf = async () => {
    if (exportPdfBusy) return;
    setExportPdfError(null);
    setExportPdfBusy(true);
    try {
      const scope =
        gstMonthMode === 'month'
          ? gstMonthYMD || 'month'
          : gstMonthMode === 'financial_year'
            ? `fy_${gstFinancialYear}`
            : 'all';
      const periodLabel =
        gstMonthMode === 'month'
          ? `Month: ${gstMonthYMD || '—'}`
          : gstMonthMode === 'financial_year'
            ? `Financial Year: ${gstFinancialYear}`
            : 'All';
      const safe = (value: any) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      const tableRowsHtml = shownGstRows
        .map(
          (row, idx) => `
            <tr>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:center;">${idx + 1}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;font-weight:700;">${safe(row.unitNo)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;">${safe(row.allotteeName === '—' ? '' : row.allotteeName)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;">${safe(row.firstReceiptDate)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;">${safe(row.dueMonth)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;">₹ ${formatAmount2(row.unitAmount)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;">${Number(row.gstPercent || 0).toFixed(2)}%</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;">₹ ${formatAmount2(row.gstAmount)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;">₹ ${formatAmount2(row.gstPaid)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;">₹ ${formatAmount2(row.balance)}</td>
              <td style="border:1px solid #bbb;padding:5px 6px;text-align:center;">${safe(row.status)}</td>
            </tr>
          `
        )
        .join('');
      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;color:#111;">
          <div style="margin-bottom:8px;">
            <div style="font-size:20px;font-weight:800;">GST Report</div>
            <div style="font-size:12px;margin-top:2px;">${safe(periodLabel)}</div>
            <div style="font-size:12px;">Units: ${gstSummary.unitsCount} &nbsp;|&nbsp; GST Amount: ₹ ${formatAmount2(gstSummary.totalGst)} &nbsp;|&nbsp; Paid: ₹ ${formatAmount2(gstSummary.totalPaid)} &nbsp;|&nbsp; Balance: ₹ ${formatAmount2(gstSummary.totalBalance)}</div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:9px;table-layout:fixed;">
            <colgroup>
              <col style="width:4%;" />
              <col style="width:9%;" />
              <col style="width:20%;" />
              <col style="width:10%;" />
              <col style="width:10%;" />
              <col style="width:11%;" />
              <col style="width:7%;" />
              <col style="width:10%;" />
              <col style="width:9%;" />
              <col style="width:9%;" />
              <col style="width:7%;" />
            </colgroup>
            <thead>
              <tr style="background:#f2f4f7;">
                <th style="border:1px solid #bbb;padding:5px 6px;">#</th>
                <th style="border:1px solid #bbb;padding:5px 6px;">Unit No</th>
                <th style="border:1px solid #bbb;padding:5px 6px;">Allottee Name</th>
                <th style="border:1px solid #bbb;padding:5px 6px;">First Receipt</th>
                <th style="border:1px solid #bbb;padding:5px 6px;">GST Due Month</th>
                <th style="border:1px solid #bbb;padding:5px 6px;text-align:right;">Unit Amount</th>
                <th style="border:1px solid #bbb;padding:5px 6px;text-align:right;">GST %</th>
                <th style="border:1px solid #bbb;padding:5px 6px;text-align:right;">GST Amount</th>
                <th style="border:1px solid #bbb;padding:5px 6px;text-align:right;">GST Paid</th>
                <th style="border:1px solid #bbb;padding:5px 6px;text-align:right;">Balance</th>
                <th style="border:1px solid #bbb;padding:5px 6px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${
                tableRowsHtml ||
                '<tr><td colspan="11" style="border:1px solid #bbb;padding:10px;text-align:center;">No GST rows for selected filter.</td></tr>'
              }
              <tr>
                <td colspan="7" style="border:1px solid #bbb;padding:5px 6px;text-align:right;font-weight:800;">Total</td>
                <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;font-weight:800;">₹ ${formatAmount2(gstSummary.totalGst)}</td>
                <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;font-weight:800;">₹ ${formatAmount2(gstSummary.totalPaid)}</td>
                <td style="border:1px solid #bbb;padding:5px 6px;text-align:right;font-weight:800;">₹ ${formatAmount2(gstSummary.totalBalance)}</td>
                <td style="border:1px solid #bbb;padding:5px 6px;"></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
      const fileBase = `gst_report_${String(projectMeta?.name || currentProjectId || 'project').replace(/\s+/g, '_')}_${scope}`;
      const payload = buildPdfPayloadFromTemplate({
        html,
        tokenValues: {},
        visualModel: {
          showTitle: false,
          showRefNo: false,
          showDate: false,
          showPageNumbers: true,
          backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
          backgroundOpacity: Number(projectMeta?.backgroundOpacity || 0.14),
          backgroundSize: 'contain',
          backgroundScale: 70,
          backgroundPosX: 50,
          backgroundPosY: 50,
          companyLogoHeightCm: 1.25,
          projectLogoHeightCm: 1.25,
        },
        companyLogoUrl: String(companyMeta?.logoUrl || projectMeta?.logoUrl || ''),
        projectLogoUrl: String(projectMeta?.logoUrl || ''),
        companyAddress: String(companyMeta?.address || ''),
        companyMobile: String(companyMeta?.mobile || ''),
        companyEmail: String(companyMeta?.email || ''),
        companyFontFamily: String(companyMeta?.fontFamily || ''),
        companyFontUrl: String(companyMeta?.fontUrl || ''),
        companyFontFormat: String(companyMeta?.fontFormat || ''),
        gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
        paperSize: 'A4',
        fileName: fileBase,
      });
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf(payload);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileBase}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (err: any) {
      setExportPdfError(err?.message || 'Failed to export PDF.');
    } finally {
      setExportPdfBusy(false);
    }
  };

  const exportContactListPdf = async () => {
    if (exportPdfBusy) return;
    setExportPdfError(null);
    setExportPdfBusy(true);
    try {
      const safe = (value: any) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      const tableRowsHtml = filteredContactRows
        .map(
          (row, idx) => `
            <tr>
              <td style="border:1px solid #bbb;padding:10px 7px;text-align:center;min-height:28px;">${idx + 1}</td>
              <td style="border:1px solid #bbb;padding:10px 7px;font-weight:700;white-space:nowrap;min-height:28px;">${safe(row.unitNo)}</td>
              <td style="border:1px solid #bbb;padding:10px 7px;min-height:28px;">${safe(row.name)}</td>
              <td style="border:1px solid #bbb;padding:10px 7px;white-space:nowrap;min-height:28px;">${safe(row.mobile)}</td>
            </tr>
          `
        )
        .join('');
      const projectName = String(projectMeta?.name || currentProjectId || 'Project').trim();
      const filterLabel = [
        `Building: ${contactBuildingFilter === 'all' ? 'All' : contactBuildingFilter}`,
        `Usage: ${contactUsageFilter === 'all' ? 'All' : contactUsageFilter}`,
      ].join(' | ');
      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;color:#111;">
          <div style="margin-bottom:8px;">
            <div style="font-size:20px;font-weight:800;">Unit Contact List</div>
            <div style="font-size:12px;margin-top:2px;">Project: ${safe(projectName)}</div>
            <div style="font-size:12px;">${safe(filterLabel)}</div>
            <div style="font-size:12px;">Units: ${filteredContactRows.length}</div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed;">
            <colgroup>
              <col style="width:7%;" />
              <col style="width:18%;" />
              <col style="width:48%;" />
              <col style="width:27%;" />
            </colgroup>
            <thead>
              <tr style="background:#f2f4f7;">
                <th style="border:1px solid #bbb;padding:6px 8px;">#</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Unit Number</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Name</th>
                <th style="border:1px solid #bbb;padding:6px 8px;">Mobile Number</th>
              </tr>
            </thead>
            <tbody>${tableRowsHtml || '<tr><td colspan="4" style="border:1px solid #bbb;padding:10px;text-align:center;">No rows found.</td></tr>'}</tbody>
          </table>
        </div>
      `;
      const fileBase = `unit_contact_list_${String(projectName || 'project').replace(/\s+/g, '_')}_${new Date()
        .toISOString()
        .slice(0, 10)}`;
      const payload = buildPdfPayloadFromTemplate({
        html,
        tokenValues: {},
        visualModel: {
          showTitle: false,
          showRefNo: false,
          showDate: false,
          showPageNumbers: true,
          backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
          backgroundOpacity: Number(projectMeta?.backgroundOpacity || 0.14),
          backgroundSize: 'contain',
          backgroundScale: 70,
          backgroundPosX: 50,
          backgroundPosY: 50,
          companyLogoHeightCm: 1.25,
          projectLogoHeightCm: 1.25,
        },
        companyLogoUrl: String(companyMeta?.logoUrl || projectMeta?.logoUrl || ''),
        projectLogoUrl: String(projectMeta?.logoUrl || ''),
        companyAddress: String(companyMeta?.address || ''),
        companyMobile: String(companyMeta?.mobile || ''),
        companyEmail: String(companyMeta?.email || ''),
        companyFontFamily: String(companyMeta?.fontFamily || ''),
        companyFontUrl: String(companyMeta?.fontUrl || ''),
        companyFontFormat: String(companyMeta?.fontFormat || ''),
        gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
        paperSize: 'A4',
        fileName: fileBase,
      });
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf(payload);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileBase}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (err: any) {
      setExportPdfError(err?.message || 'Failed to export PDF.');
    } finally {
      setExportPdfBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
            {reportType === 'booking'
              ? 'Booking MIS'
              : reportType === 'rera'
              ? 'RERA Booking Report'
              : reportType === 'gst'
              ? 'GST Report'
              : 'Unit Contact List'}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: { xs: '100%', lg: 'auto' }, alignSelf: { xs: 'stretch', lg: 'auto' } }}
          >
            {isCompactLayout ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="report-type-label">Report</InputLabel>
                <Select
                  labelId="report-type-label"
                  label="Report"
                  value={reportType}
                  onChange={(e) => {
                    const next = String(e.target.value || 'booking');
                    setReportType(
                      next === 'rera' || next === 'gst' || next === 'contacts' ? (next as any) : 'booking'
                    );
                  }}
                >
                  <MenuItem value="booking">Booking MIS</MenuItem>
                  <MenuItem value="rera">RERA Booking Report</MenuItem>
                  <MenuItem value="gst">GST Report</MenuItem>
                  <MenuItem value="contacts">Unit Contact List</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <ToggleButtonGroup
                exclusive
                size={isTablet ? 'small' : 'medium'}
                value={reportType}
                onChange={(_, next) => {
                  if (!next) return;
                  setReportType(next);
                }}
                sx={{
                  mr: { md: 1 },
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                  '& .MuiToggleButton-root': {
                    whiteSpace: 'nowrap',
                    px: isTablet ? 1.25 : 1.75,
                    py: isTablet ? 0.5 : 0.75,
                    fontSize: isTablet ? 13 : 14,
                  },
                }}
              >
                <ToggleButton value="booking">Booking MIS</ToggleButton>
                <ToggleButton value="rera">RERA Booking Report</ToggleButton>
                <ToggleButton value="gst">GST Report</ToggleButton>
                <ToggleButton value="contacts">Contact List</ToggleButton>
              </ToggleButtonGroup>
            )}
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              fullWidth={isCompactLayout}
              onClick={() => {
                setExportPdfError(null);
                setExportDialogOpen(true);
              }}
              disabled={
                reportType === 'booking'
                  ? !filteredRows.length
                  : reportType === 'rera'
                  ? !shownReraRows.length
                  : reportType === 'gst'
                  ? !shownGstRows.length
                  : !filteredContactRows.length
              }
            >
              Export
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.25 }}>
          <TextField
            fullWidth
            label={
              reportType === 'gst'
                ? 'Search unit/name/status'
                : reportType === 'contacts'
                ? 'Search unit/name/mobile'
                : 'Search unit/name/KYC'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {reportType === 'gst' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="gst-month-mode-label">View</InputLabel>
                <Select
                  labelId="gst-month-mode-label"
                  label="View"
                  value={gstMonthMode}
                  onChange={(e) => {
                    const next = String(e.target.value || 'all');
                    setGstMonthMode(next === 'month' || next === 'financial_year' ? next : 'all');
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="financial_year">Financial Year</MenuItem>
                </Select>
              </FormControl>
              {gstMonthMode === 'month' ? (
                <TextField
                  size="small"
                  label="Month"
                  type="month"
                  value={gstMonthYMD}
                  onChange={(e) => setGstMonthYMD(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' } }}
                />
              ) : gstMonthMode === 'financial_year' ? (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' } }}>
                  <InputLabel id="gst-financial-year-label">Financial Year</InputLabel>
                  <Select
                    labelId="gst-financial-year-label"
                    label="Financial Year"
                    value={gstFinancialYear}
                    onChange={(e) => setGstFinancialYear(String(e.target.value || getCurrentFinancialYearKey()))}
                  >
                    {gstFinancialYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Stack>
          ) : reportType === 'booking' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="booking-period-mode-label">Period</InputLabel>
                <Select
                  labelId="booking-period-mode-label"
                  label="Period"
                  value={bookingPeriodMode}
                  onChange={(e) => {
                    const next = String(e.target.value || 'till_date');
                    setBookingPeriodMode(next === 'financial_year' ? 'financial_year' : 'till_date');
                    setSelectedBookingTrendCutoffMs(null);
                  }}
                >
                  <MenuItem value="till_date">Till Date</MenuItem>
                  <MenuItem value="financial_year">Financial Year</MenuItem>
                </Select>
              </FormControl>
              {bookingPeriodMode === 'financial_year' ? (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' } }}>
                  <InputLabel id="booking-financial-year-label">Financial Year</InputLabel>
                  <Select
                    labelId="booking-financial-year-label"
                    label="Financial Year"
                    value={bookingFinancialYear}
                    onChange={(e) => {
                      setBookingFinancialYear(String(e.target.value || getCurrentFinancialYearKey()));
                      setSelectedBookingTrendCutoffMs(null);
                    }}
                  >
                    {financialYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  size="small"
                  label="Till Date"
                  type="date"
                  value={exportTillDateYMD}
                  onChange={(e) => setExportTillDateYMD(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 190 }, width: { xs: '100%', sm: 'auto' } }}
                />
              )}
            </Stack>
          ) : reportType === 'rera' ? (
            <TextField
              size="small"
              label="Till Date"
              type="date"
              value={exportTillDateYMD}
              onChange={(e) => setExportTillDateYMD(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 190 }, width: { xs: '100%', sm: 'auto' } }}
            />
          ) : reportType === 'contacts' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="contact-building-filter-label">Building</InputLabel>
                <Select
                  labelId="contact-building-filter-label"
                  label="Building"
                  value={contactBuildingFilter}
                  onChange={(e) => setContactBuildingFilter(String(e.target.value || 'all'))}
                >
                  <MenuItem value="all">All Buildings</MenuItem>
                  {contactBuildingOptions.map((building) => (
                    <MenuItem key={building} value={building}>
                      {building}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="contact-usage-filter-label">Usage</InputLabel>
                <Select
                  labelId="contact-usage-filter-label"
                  label="Usage"
                  value={contactUsageFilter}
                  onChange={(e) => setContactUsageFilter(String(e.target.value || 'all'))}
                >
                  <MenuItem value="all">All Usage</MenuItem>
                  {contactUsageOptions.map((usage) => (
                    <MenuItem key={usage} value={usage}>
                      {usage}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : null}
        </Stack>

        {reportType === 'booking' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 180 }, flex: { xs: '0 0 auto', sm: '1 1 180px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Units
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {shownUnitsCount}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 260 }, flex: { xs: '0 0 auto', sm: '1 1 260px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Received Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(shownReceivedAmount)}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 260 }, flex: { xs: '0 0 auto', sm: '1 1 260px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Sold Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(shownSoldAmount)}
              </Typography>
            </Paper>
          </Stack>
        ) : reportType === 'rera' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 180 }, flex: { xs: '0 0 auto', sm: '1 1 180px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Units
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {shownReraUnitsCount}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 260 }, flex: { xs: '0 0 auto', sm: '1 1 260px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Received Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(shownReraReceivedAmount)}
              </Typography>
            </Paper>
          </Stack>
        ) : reportType === 'gst' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
            <Paper
              variant="outlined"
              onClick={() => setGstTopFilter('all')}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                minWidth: { sm: 180 },
                flex: { xs: '0 0 auto', sm: '1 1 180px' },
                cursor: 'pointer',
                borderColor: gstTopFilter === 'all' ? 'primary.main' : undefined,
                bgcolor: gstTopFilter === 'all' ? 'primary.50' : undefined,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Units Due
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {gstSummary.unitsCount}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              onClick={() => setGstTopFilter('amount')}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                minWidth: { sm: 260 },
                flex: { xs: '0 0 auto', sm: '1 1 260px' },
                cursor: 'pointer',
                borderColor: gstTopFilter === 'amount' ? 'primary.main' : undefined,
                bgcolor: gstTopFilter === 'amount' ? 'primary.50' : undefined,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                GST Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(gstSummary.totalGst)}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              onClick={() => setGstTopFilter('paid')}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                minWidth: { sm: 260 },
                flex: { xs: '0 0 auto', sm: '1 1 260px' },
                cursor: 'pointer',
                borderColor: gstTopFilter === 'paid' ? 'primary.main' : undefined,
                bgcolor: gstTopFilter === 'paid' ? 'primary.50' : undefined,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                GST Paid
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(gstSummary.totalPaid)}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              onClick={() => setGstTopFilter('balance')}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                minWidth: { sm: 260 },
                flex: { xs: '0 0 auto', sm: '1 1 260px' },
                cursor: 'pointer',
                borderColor: gstTopFilter === 'balance' ? 'primary.main' : undefined,
                bgcolor: gstTopFilter === 'balance' ? 'primary.50' : undefined,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                GST Balance
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                ₹ {formatAmount2(gstSummary.totalBalance)}
              </Typography>
            </Paper>
          </Stack>
        ) : reportType === 'contacts' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 180 }, flex: { xs: '0 0 auto', sm: '1 1 180px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                Units
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {filteredContactRows.length}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: { sm: 180 }, flex: { xs: '0 0 auto', sm: '1 1 180px' } }}
            >
              <Typography variant="caption" color="text.secondary">
                With Mobile
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {filteredContactRows.filter((row) => !!row.mobile).length}
              </Typography>
            </Paper>
          </Stack>
        ) : null}
        {reportType === 'booking' ? (
          <Paper variant="outlined" sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Booking vs Sold Trend ({bookingTrend.mode === 'day' ? 'Daily' : 'Monthly'} cumulative)
              </Typography>
              {bookingTrend.points.length ? (
                <Typography variant="caption" color="text.secondary">
                  Peak ₹ {formatAmount2(bookingTrendSvg?.maxValue || 0)}
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                <span style={{ color: '#1976d2', fontWeight: 700 }}>●</span> Received
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <span style={{ color: '#2e7d32', fontWeight: 700 }}>●</span> Sold
              </Typography>
            </Stack>
            {bookingTrend.points.length && bookingTrendSvg ? (
              <Box sx={{ width: '100%', height: 120, position: 'relative' }}>
                <svg
                  viewBox={`0 0 ${bookingTrendSvg.width} ${bookingTrendSvg.height}`}
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                  onMouseMove={(e) => {
                    if (!bookingTrendSvg.coords?.length) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    if (!rect.width) return;
                    const xPercent = (e.clientX - rect.left) / rect.width;
                    const xValue = Math.max(0, Math.min(bookingTrendSvg.width, xPercent * bookingTrendSvg.width));
                    let nearestIdx = 0;
                    let nearestDist = Number.POSITIVE_INFINITY;
                    bookingTrendSvg.coords.forEach((coord, idx) => {
                      const dist = Math.abs(coord.x - xValue);
                      if (dist < nearestDist) {
                        nearestDist = dist;
                        nearestIdx = idx;
                      }
                    });
                    setBookingTrendHoverIndex(nearestIdx);
                  }}
                  onMouseLeave={() => setBookingTrendHoverIndex(null)}
                  onClick={(e) => {
                    if (!bookingTrendSvg.coords?.length) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    if (!rect.width) return;
                    const xPercent = (e.clientX - rect.left) / rect.width;
                    const xValue = Math.max(0, Math.min(bookingTrendSvg.width, xPercent * bookingTrendSvg.width));
                    let nearestIdx = 0;
                    let nearestDist = Number.POSITIVE_INFINITY;
                    bookingTrendSvg.coords.forEach((coord, idx) => {
                      const dist = Math.abs(coord.x - xValue);
                      if (dist < nearestDist) {
                        nearestDist = dist;
                        nearestIdx = idx;
                      }
                    });
                    setBookingTrendSelectedIndex((prev) => {
                      const next = prev === nearestIdx ? null : nearestIdx;
                      setSelectedBookingTrendCutoffMs(
                        next === null ? null : Number(bookingTrendSvg.coords[next]?.cutoffMs || 0) || null
                      );
                      return next;
                    });
                  }}
                >
                  <line x1="0" y1={bookingTrendSvg.height - 18} x2={bookingTrendSvg.width} y2={bookingTrendSvg.height - 18} stroke="#e0e0e0" strokeWidth="1" />
                  <polyline
                    fill="none"
                    stroke="#1976d2"
                    strokeWidth="2.2"
                    points={bookingTrendSvg.receivedPath}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    fill="none"
                    stroke="#2e7d32"
                    strokeWidth="2.2"
                    points={bookingTrendSvg.soldPath}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {bookingTrendSvg.tickIndices.map((idx) => {
                    const coord = bookingTrendSvg.coords[idx];
                    const anchor = idx === 0 ? 'start' : idx === bookingTrendSvg.coords.length - 1 ? 'end' : 'middle';
                    return (
                      <text key={`tick-${idx}`} x={coord.x} y={bookingTrendSvg.height - 4} textAnchor={anchor} fill="#6b7280" fontSize="10">
                        {coord.label}
                      </text>
                    );
                  })}
                  {bookingTrendSelectedIndex !== null && bookingTrendSvg.coords[bookingTrendSelectedIndex] ? (
                    <>
                      <line
                        x1={bookingTrendSvg.coords[bookingTrendSelectedIndex].x}
                        y1={8}
                        x2={bookingTrendSvg.coords[bookingTrendSelectedIndex].x}
                        y2={bookingTrendSvg.height - 18}
                        stroke="#1976d2"
                        strokeWidth="1.2"
                        strokeDasharray="3,3"
                      />
                      <circle
                        cx={bookingTrendSvg.coords[bookingTrendSelectedIndex].x}
                        cy={bookingTrendSvg.coords[bookingTrendSelectedIndex].yReceived}
                        r="4.8"
                        fill="#1976d2"
                        stroke="#fff"
                        strokeWidth="1.8"
                      />
                      <circle
                        cx={bookingTrendSvg.coords[bookingTrendSelectedIndex].x}
                        cy={bookingTrendSvg.coords[bookingTrendSelectedIndex].ySold}
                        r="4.8"
                        fill="#2e7d32"
                        stroke="#fff"
                        strokeWidth="1.8"
                      />
                    </>
                  ) : null}
                  {bookingTrendHoverIndex !== null && bookingTrendSvg.coords[bookingTrendHoverIndex] ? (
                    <>
                      <line
                        x1={bookingTrendSvg.coords[bookingTrendHoverIndex].x}
                        y1={8}
                        x2={bookingTrendSvg.coords[bookingTrendHoverIndex].x}
                        y2={bookingTrendSvg.height - 18}
                        stroke="#90caf9"
                        strokeWidth="1"
                        strokeDasharray="2,3"
                      />
                      <circle
                        cx={bookingTrendSvg.coords[bookingTrendHoverIndex].x}
                        cy={bookingTrendSvg.coords[bookingTrendHoverIndex].yReceived}
                        r="4"
                        fill="#1976d2"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx={bookingTrendSvg.coords[bookingTrendHoverIndex].x}
                        cy={bookingTrendSvg.coords[bookingTrendHoverIndex].ySold}
                        r="4"
                        fill="#2e7d32"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                    </>
                  ) : null}
                </svg>
                {bookingTrendSelectedIndex !== null && bookingTrendSvg.coords[bookingTrendSelectedIndex] ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 8,
                      bottom: 4,
                      fontSize: 11,
                      color: 'primary.main',
                      bgcolor: 'rgba(25,118,210,0.08)',
                      px: 0.8,
                      py: 0.3,
                      borderRadius: 1,
                    }}
                  >
                    Filtered to: {bookingTrendSvg.coords[bookingTrendSelectedIndex].fullLabel}
                  </Box>
                ) : null}
                {bookingTrendHoverIndex !== null && bookingTrendSvg.coords[bookingTrendHoverIndex] ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${(bookingTrendSvg.coords[bookingTrendHoverIndex].x / bookingTrendSvg.width) * 100}%`,
                      top: `${Math.max(
                        6,
                        (Math.min(
                          bookingTrendSvg.coords[bookingTrendHoverIndex].yReceived,
                          bookingTrendSvg.coords[bookingTrendHoverIndex].ySold
                        ) /
                          bookingTrendSvg.height) *
                          100 -
                          10
                      )}%`,
                      transform: 'translate(-50%, -100%)',
                      bgcolor: 'rgba(17,24,39,0.92)',
                      color: '#fff',
                      px: 0.9,
                      py: 0.6,
                      borderRadius: 1,
                      fontSize: 11,
                      lineHeight: 1.25,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
                    }}
                  >
                    <div>{bookingTrendSvg.coords[bookingTrendHoverIndex].fullLabel}</div>
                    <div>Received: ₹ {formatAmount2(bookingTrendSvg.coords[bookingTrendHoverIndex].receivedValue)}</div>
                    <div>Sold: ₹ {formatAmount2(bookingTrendSvg.coords[bookingTrendHoverIndex].soldValue)}</div>
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No receipt data in selected date range.
              </Typography>
            )}
          </Paper>
        ) : null}

        {loadError ? (
          <Alert severity="error" sx={{ mt: 1.25 }}>
            {loadError}
          </Alert>
        ) : null}

        {reportType === 'booking' ? (
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.25 }}>
            <Button
              size="small"
              variant={bookingLoanOnly ? 'contained' : 'outlined'}
              onClick={() => setBookingLoanOnly((current) => !current)}
            >
              Loan Units
            </Button>
            <IconButton
              aria-label="Choose Booking MIS columns"
              title="Choose columns"
              onClick={(event) => setBookingColumnAnchor(event.currentTarget)}
              sx={{ border: '1px solid', borderColor: 'primary.main', color: 'primary.main', borderRadius: 1 }}
            >
              <TuneOutlinedIcon />
            </IconButton>
          </Stack>
        ) : null}

        <TableContainer sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
          {reportType === 'booking' ? (
            <Table size="small" sx={{ minWidth: 860 + (bookingColumns.totalAmount ? 140 : 0) + (bookingColumns.pendingAmount ? 150 : 0) + (bookingColumns.gstStatus ? 120 : 0) }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      position: { xs: 'sticky', md: 'static' },
                      left: { xs: 0, md: 'auto' },
                      zIndex: { xs: 4, md: 'auto' },
                      bgcolor: 'grey.100',
                      minWidth: 96,
                    }}
                  >
                    Unit No
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Date of Agreement</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>KYC Type</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>KYC No</TableCell>
                  {bookingColumns.totalAmount ? <TableCell sx={{ fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap' }}>Total Amount</TableCell> : null}
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>Received Amount</TableCell>
                  {bookingColumns.pendingAmount ? <TableCell sx={{ fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap' }}>Pending Amount</TableCell> : null}
                  {bookingColumns.gstStatus ? <TableCell sx={{ fontWeight: 800, textAlign: 'center', whiteSpace: 'nowrap' }}>GST Status</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6 + Number(bookingColumns.totalAmount) + Number(bookingColumns.pendingAmount) + Number(bookingColumns.gstStatus)}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading booking MIS…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length ? (
                  filteredRows.map((row) => (
                    <TableRow
                      key={row.unitId}
                      hover
                      onClick={() => {
                        const unit = units.find((u) => String(u.id || '') === String(row.unitId || '')) || null;
                        if (!unit) return;
                        setBookingUnit(unit);
                        setBookingDrawerOpen(true);
                      }}
                      sx={() => {
                        const isSelected = bookingUnit?.id === row.unitId;
                        const isOverReceived = row.unitAmount > 0 && row.receivedAmount > row.unitAmount;
                        const isPaymentComplete = row.unitAmount > 0 && row.receivedAmount === row.unitAmount;
                        return {
                          cursor: 'pointer',
                          bgcolor: isSelected
                            ? 'rgba(25, 118, 210, 0.08)'
                            : isOverReceived
                              ? 'rgba(244, 67, 54, 0.08)'
                              : isPaymentComplete
                                ? 'rgba(76, 175, 80, 0.12)'
                              : undefined,
                          '&:hover': {
                            bgcolor: isSelected
                              ? 'rgba(25, 118, 210, 0.12)'
                              : isOverReceived
                                ? 'rgba(244, 67, 54, 0.12)'
                                : isPaymentComplete
                                  ? 'rgba(76, 175, 80, 0.18)'
                                : undefined,
                          },
                        };
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          zIndex: { xs: 3, md: 'auto' },
                          bgcolor: 'background.paper',
                          minWidth: 96,
                        }}
                      >
                        {row.unitNo}
                      </TableCell>
                      <TableCell>{row.names}</TableCell>
                      <TableCell>{row.agreementDate}</TableCell>
                      <TableCell>{row.kycType}</TableCell>
                      <TableCell>{row.kycNo}</TableCell>
                      {bookingColumns.totalAmount ? <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.unitAmount)}</TableCell> : null}
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.receivedAmount)}</TableCell>
                      {bookingColumns.pendingAmount ? <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.pendingAmount)}</TableCell> : null}
                      {bookingColumns.gstStatus ? (
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.gstStatus === 'paid' ? (
                            <CheckCircleOutlineIcon titleAccess="GST received" sx={{ color: 'success.main', fontSize: 20, verticalAlign: 'middle' }} />
                          ) : (
                            <CancelOutlinedIcon titleAccess="GST pending" sx={{ color: 'error.main', fontSize: 20, verticalAlign: 'middle' }} />
                          )}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6 + Number(bookingColumns.totalAmount) + Number(bookingColumns.pendingAmount) + Number(bookingColumns.gstStatus)} sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : reportType === 'rera' ? (
            <Table size="small" sx={{ minWidth: 1500, tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>BLOCK</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      position: { xs: 'sticky', md: 'static' },
                      left: { xs: 0, md: 'auto' },
                      zIndex: { xs: 4, md: 'auto' },
                      bgcolor: 'grey.100',
                      minWidth: 96,
                    }}
                  >
                    UNIT NO
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>USAGE</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>CARPET AREA</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>EXCLUSIVE AREA</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1.2 }}>UNIT AMOUNT</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1.2 }}>RECEIVED AMOUNT</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>AGREEMENT DATE</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>ENCUMBRANCE STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>ALLOTEE NAME</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>KYC TYPE</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>KYC ID</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>MOBILE NUMBER</TableCell>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>REDEVELOPMENT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={15}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading RERA report…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : shownReraRows.length ? (
                  shownReraRows.map((row) => (
                    <TableRow
                      key={row.unitId}
                      hover
                      onClick={() => {
                        const unit = units.find((u) => String(u.id || '') === String(row.unitId || '')) || null;
                        if (!unit) return;
                        setBookingUnit(unit);
                        setBookingDrawerOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{row.blockType}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          zIndex: { xs: 3, md: 'auto' },
                          bgcolor: 'background.paper',
                          minWidth: 96,
                        }}
                      >
                        {row.flatNo}
                      </TableCell>
                      <TableCell>{row.usage}</TableCell>
                      <TableCell>{row.carpetArea > 0 ? row.carpetArea : 0}</TableCell>
                      <TableCell>
                        {Number(row.areaExclusive || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.unitAmount)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.receivedAmount)}</TableCell>
                      <TableCell>{row.agreementDate === '—' ? '' : row.agreementDate}</TableCell>
                      <TableCell>{row.encumbranceStatus}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {(row.allotteeNames || []).length
                          ? (row.allotteeNames || []).map((name: string, idx: number) => (
                              <Box key={`${row.unitId}-name-${idx}`} component="div">
                                {name}
                              </Box>
                            ))
                          : row.allotteeName === '—'
                          ? ''
                          : row.allotteeName}
                      </TableCell>
                      <TableCell>{row.kycType === '—' ? '' : row.kycType}</TableCell>
                      <TableCell>{row.kycNo === '—' ? '' : row.kycNo}</TableCell>
                      <TableCell>{row.mobileNumber === '—' ? '' : row.mobileNumber}</TableCell>
                      <TableCell>{row.redevelopment}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                  <TableCell colSpan={15} sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : reportType === 'contacts' ? (
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      position: { xs: 'sticky', md: 'static' },
                      left: { xs: 0, md: 'auto' },
                      zIndex: { xs: 4, md: 'auto' },
                      bgcolor: 'grey.100',
                      minWidth: 110,
                    }}
                  >
                    Unit Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Mobile Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading contact list…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : filteredContactRows.length ? (
                  filteredContactRows.map((row) => (
                    <TableRow
                      key={row.unitId}
                      hover
                      onClick={() => {
                        const unit = units.find((u) => String(u.id || '') === String(row.unitId || '')) || null;
                        if (!unit) return;
                        setBookingUnit(unit);
                        setBookingDrawerOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          zIndex: { xs: 3, md: 'auto' },
                          bgcolor: 'background.paper',
                          minWidth: 110,
                        }}
                      >
                        {row.unitNo}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.mobile}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No contact rows found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      position: { xs: 'sticky', md: 'static' },
                      left: { xs: 0, md: 'auto' },
                      zIndex: { xs: 4, md: 'auto' },
                      bgcolor: 'grey.100',
                      minWidth: 96,
                    }}
                  >
                    Unit No
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Allottee Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>First Receipt Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>GST Due Month</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>Unit Amount</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>GST %</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>GST Amount</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>GST Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading GST report…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : shownGstRows.length ? (
                  shownGstRows.map((row) => (
                    <TableRow
                      key={row.unitId}
                      hover
                      onClick={() => {
                        const unit = units.find((u) => String(u.id || '') === String(row.unitId || '')) || null;
                        if (!unit) return;
                        setBookingUnit(unit);
                        setBookingDrawerOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          zIndex: { xs: 3, md: 'auto' },
                          bgcolor: 'background.paper',
                          minWidth: 96,
                        }}
                      >
                        {row.unitNo}
                      </TableCell>
                      <TableCell>{row.allotteeName === '—' ? '' : row.allotteeName}</TableCell>
                      <TableCell>{row.firstReceiptDate}</TableCell>
                      <TableCell>{row.dueMonth}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.unitAmount)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{Number(row.gstPercent || 0).toFixed(2)}%</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.gstAmount)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAmount2(row.gstPaid)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No GST due units found for selected filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <Popover
          open={Boolean(bookingColumnAnchor)}
          anchorEl={bookingColumnAnchor}
          onClose={() => setBookingColumnAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack spacing={0.25} sx={{ p: 1.25, minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, px: 1 }}>Display columns</Typography>
            <FormControlLabel
              control={<Checkbox checked={bookingColumns.totalAmount} onChange={(event) => setBookingColumns((current) => ({ ...current, totalAmount: event.target.checked }))} />}
              label="Total Amount"
            />
            <FormControlLabel
              control={<Checkbox checked={bookingColumns.pendingAmount} onChange={(event) => setBookingColumns((current) => ({ ...current, pendingAmount: event.target.checked }))} />}
              label="Pending Amount"
            />
            <FormControlLabel
              control={<Checkbox checked={bookingColumns.gstStatus} onChange={(event) => setBookingColumns((current) => ({ ...current, gstStatus: event.target.checked }))} />}
              label="GST Status"
            />
          </Stack>
        </Popover>
      </Paper>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {reportType === 'booking'
            ? 'Export Booking MIS'
            : reportType === 'rera'
            ? 'Export RERA Booking Report'
            : reportType === 'contacts'
            ? 'Export Unit Contact List'
            : 'Export GST Report'}
        </DialogTitle>
        <DialogContent dividers>
          {reportType === 'gst' ? (
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="gst-export-month-mode-label">View</InputLabel>
                <Select
                  labelId="gst-export-month-mode-label"
                  label="View"
                  value={gstMonthMode}
                  onChange={(e) => {
                    const next = String(e.target.value || 'all');
                    setGstMonthMode(next === 'month' || next === 'financial_year' ? next : 'all');
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="financial_year">Financial Year</MenuItem>
                </Select>
              </FormControl>
              {gstMonthMode === 'month' ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Month"
                  type="month"
                  value={gstMonthYMD}
                  onChange={(e) => setGstMonthYMD(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : gstMonthMode === 'financial_year' ? (
                <FormControl size="small" fullWidth>
                  <InputLabel id="gst-export-financial-year-label">Financial Year</InputLabel>
                  <Select
                    labelId="gst-export-financial-year-label"
                    label="Financial Year"
                    value={gstFinancialYear}
                    onChange={(e) => setGstFinancialYear(String(e.target.value || getCurrentFinancialYearKey()))}
                  >
                    {gstFinancialYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Stack>
          ) : reportType === 'booking' ? (
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="booking-export-period-mode-label">Period</InputLabel>
                <Select
                  labelId="booking-export-period-mode-label"
                  label="Period"
                  value={bookingPeriodMode}
                  onChange={(e) => {
                    const next = String(e.target.value || 'till_date');
                    setBookingPeriodMode(next === 'financial_year' ? 'financial_year' : 'till_date');
                    setSelectedBookingTrendCutoffMs(null);
                  }}
                >
                  <MenuItem value="till_date">Till Date</MenuItem>
                  <MenuItem value="financial_year">Financial Year</MenuItem>
                </Select>
              </FormControl>
              {bookingPeriodMode === 'financial_year' ? (
                <FormControl size="small" fullWidth>
                  <InputLabel id="booking-export-financial-year-label">Financial Year</InputLabel>
                  <Select
                    labelId="booking-export-financial-year-label"
                    label="Financial Year"
                    value={bookingFinancialYear}
                    onChange={(e) => {
                      setBookingFinancialYear(String(e.target.value || getCurrentFinancialYearKey()));
                      setSelectedBookingTrendCutoffMs(null);
                    }}
                  >
                    {financialYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Till Date"
                  type="date"
                  value={exportTillDateYMD}
                  onChange={(e) => setExportTillDateYMD(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingExportColumns.kycType}
                    onChange={(event) => setBookingExportColumns((current) => ({ ...current, kycType: event.target.checked }))}
                  />
                }
                label="Include KYC Type"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingExportColumns.kycNo}
                    onChange={(event) => setBookingExportColumns((current) => ({ ...current, kycNo: event.target.checked }))}
                  />
                }
                label="Include KYC Number"
              />
            </Stack>
          ) : reportType === 'contacts' ? (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Export {filteredContactRows.length} units with primary contact name and mobile number.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Building: {contactBuildingFilter === 'all' ? 'All' : contactBuildingFilter} · Usage:{' '}
                {contactUsageFilter === 'all' ? 'All' : contactUsageFilter}
              </Typography>
            </Stack>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Till Date"
              type="date"
              value={exportTillDateYMD}
              onChange={(e) => setExportTillDateYMD(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {(reportType === 'booking' || reportType === 'gst' || reportType === 'contacts') && exportPdfError ? (
            <Alert severity="error" sx={{ mt: 1.25 }}>
              {exportPdfError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={
              reportType === 'booking'
                ? exportBookingMisCsv
                : reportType === 'rera'
                ? exportReraBookingXlsx
                : reportType === 'contacts'
                ? exportContactListCsv
                : exportGstCsv
            }
          >
            {reportType === 'rera' ? 'Export XLSX' : 'Export CSV'}
          </Button>
          {reportType === 'booking' || reportType === 'gst' || reportType === 'contacts' ? (
            <Button
              variant="contained"
              onClick={
                reportType === 'contacts'
                  ? exportContactListPdf
                  : reportType === 'gst'
                    ? exportGstPdf
                    : exportBookingMisPdf
              }
              disabled={exportPdfBusy}
            >
              {exportPdfBusy ? 'Exporting PDF…' : 'Export PDF'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <OfficeBookingDrawer
        open={bookingDrawerOpen}
        onClose={() => {
          setBookingDrawerOpen(false);
          setBookingUnit(null);
        }}
        onDesktopOffsetChange={() => {}}
        projectId={String(currentProjectId || '')}
        unit={bookingUnit as any}
      />
    </Box>
  );
}
